// Standalone viewer: avoid bundling the native mapbox-gl-arcgis-featureserver.
// FeatureService is only instantiated by map-drawing code paths that are
// reached exclusively through MapComponent, which is stubbed to render null.
const FeatureService = function FeatureServiceStub() {};
import { v4 as uuid } from 'uuid';
import { layerConfiguration } from './data.js';
import { fuzzy } from './fuzzy.js';
import { mergeSuggestAndGeoResults } from './mergeSuggestAndGeoResults.js';
import { PINK, ORANGE } from '../../../config/theme';
import { translate, translateText } from '../../../i18next.js';
import { escape as escapeHtml } from 'html-escaper';

const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Trailing-edge debounce. The returned wrapper delays calls to `fn` until
 * `wait` ms have elapsed since the last invocation. The most recent set of
 * arguments wins — earlier pending invocations are dropped.
 *
 * The returned function exposes a `.cancel()` method that clears any pending
 * timer without firing `fn`. Used in teardown (`destroyMap` / cleanup paths)
 * to prevent a post-unmount fire after the map modal closes.
 *
 * @param {Function} fn - Function to debounce. Called with the trailing call's
 *   arguments after `wait` ms of inactivity.
 * @param {number} wait - Debounce window in milliseconds.
 * @returns {Function & {cancel: () => void}} Debounced wrapper. Invoke as usual;
 *   call `.cancel()` to drop any pending invocation.
 * @example
 *   const debouncedSave = debounce(save, 300);
 *   debouncedSave(a); debouncedSave(b); // only `save(b)` fires, 300ms later
 *   debouncedSave.cancel();              // drops the pending call
 */
export function debounce(fn, wait) {
  let timer = null;
  const debounced = (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return debounced;
}

/**
 * Marker `dragend` handler. Reads coordinates from the dragged marker
 * (identified by `markerName`) via `getLngLat()` — Mapbox's `dragend` event
 * payload does NOT carry `lngLat` (only map `'click'` events do), so the
 * marker is the source of truth. Coordinates are validated to be finite
 * numbers within geographic bounds; on success `onDragEnd(lng, lat,
 * markerName)` is invoked so the caller can route the result back to the
 * marker that originated the drag.
 *
 * Silent no-op contract: missing `mapData`, missing `mapData[markerName]`,
 * `onDragEnd` not a function, or invalid coordinates → return without
 * throwing/logging.
 *
 * @param {Object} mapData - Per-map state bag from `mapInstanceRefs.get(...)`.
 * @param {'searchMarker'|'clickMarker'|'valueMarker'} markerName - Which
 *   marker fired the dragend. Used as the key into `mapData` and passed
 *   through to `onDragEnd` so the caller can update the correct marker.
 * @param {(lng: number, lat: number, markerName: string) => void} onDragEnd
 * @returns {void}
 */
export function handleMarkerDragEnd(mapData, markerName, onDragEnd) {
  if (!mapData || !mapData[markerName]) return;
  if (typeof onDragEnd !== 'function') return;
  const { lng, lat } = mapData[markerName].getLngLat();
  if (
    !Number.isFinite(lng) ||
    !Number.isFinite(lat) ||
    lng < -180 ||
    lng > 180 ||
    lat < -90 ||
    lat > 90
  ) {
    return;
  }
  onDragEnd(lng, lat, markerName);
}

if (!window.mapboxgl) {
  let scriptTag = document.querySelector('#mapbox-script');
  if (scriptTag) {
    scriptTag.onload = () => {
      window.mapboxgl.accessToken = accessToken;
    };
  }
} else {
  window.mapboxgl.accessToken = accessToken;
}

// Splits intersection names into their two street components.
// Matches " and " (case-insensitive, requires whitespace on both sides) or "&" (with or without spaces).
// Examples:
//   "Main St and 1st Ave"   → ["Main St", "1st Ave"]
//   "Main St AND 1st Ave"   → ["Main St", "1st Ave"]
//   "Main St & 1st Ave"     → ["Main St ", " 1st Ave"]
//   "Main St&1st Ave"       → ["Main St", "1st Ave"]
// Note: "and" requires surrounding whitespace to avoid false matches inside words like "Sandwich Blvd".
const intersectionSplitRegex = /\s+and\s+|&/i;
const sessionToken = uuid();
const clickResultsLimit = 10;
const oneMileInMeters = 1609.34;
const intersectingStreetDistanceMax = 0.0004; // ~0.02 miles (~105 ft) squared distance
const earthRadiusMeters = 6378137;

// Names of the three persistent markers managed by enableMap. Used as keys
// into `mapData` (e.g. mapData[MARKER_NAMES.SEARCH]) and as the dragSource
// identifier flowing through handleMarkerDragEnd → mapComponent →
// selectResult, where each name maps 1:1 to a setResult `type`. Centralized
// so a typo doesn't silently make a drag dispatch a no-op.
export const MARKER_NAMES = Object.freeze({
  SEARCH: 'searchMarker',
  CLICK: 'clickMarker',
  VALUE: 'valueMarker',
});

// Per-marker plumbing shared by enableMap (construction), setResult
// (result→marker dispatch), updateMarkerAffordances (active loop), and
// destroyMap (teardown). Each entry pairs a setResult `type` with the
// mapData field that holds the result for that marker, the marker name,
// and its popup field. The setResult logic used to be three near-identical
// branches; routing through this table keeps the three paths in sync.
const RESULT_TARGETS = {
  search: {
    resultField: 'searchResult',
    markerName: MARKER_NAMES.SEARCH,
    popupField: 'searchMarkerPopup',
  },
  value: {
    resultField: 'initialResult',
    markerName: MARKER_NAMES.VALUE,
    popupField: 'valueMarkerPopup',
  },
  click: {
    resultField: 'clickResult',
    markerName: MARKER_NAMES.CLICK,
    popupField: 'clickMarkerPopup',
  },
};

// Mapbox feature_type ranking used as a sort tiebreaker. When the v6 street
// geocode and the searchbox reverse endpoint return features at the same
// (or near-identical) coordinates — e.g. a 'street' feature like "Skyview
// Lane" and an 'address' feature like "3427 Skyview Lane" both centered
// on the same rooftop — a pure distance sort keeps the street ahead by
// source order, which downgrades the result officers actually save.
//
// Intersection ranks highest because intersections are only constructed
// (in `searchLngLat`) when two streets are within ~105 ft of each other at
// the drop point — which only happens when the user dropped at a corner.
// Its presence in the results is a strong signal of intent, so it should
// outrank a near-co-located 'address' (e.g. a corner house) at a tied
// distance.
export const FEATURE_SPECIFICITY = {
  intersection: 5,
  address: 4,
  poi: 3,
  street: 1,
};
// Tied-distance threshold in (miles)². 1e-8 ≈ 0.0001 mi ≈ 0.5 ft, which is
// well below any meaningful geocoder offset but tolerates floating-point
// noise between identical-coord computations from different code paths.
export const TIED_DISTANCE_THRESHOLD = 1e-8;

/**
 * Sort comparator for the unified `lookupByLngLat` ranking. Primary key is
 * planar-distance² (already computed per result); when two results land
 * within `TIED_DISTANCE_THRESHOLD` of the same point, ties are broken by
 * `FEATURE_SPECIFICITY` so the more informative feature wins (an `address`
 * with a house number beats a co-located `street` feature). Unknown types
 * default to rank 0.
 *
 * Pure function — exported so the sort behavior can be unit-tested without
 * running the surrounding network/marker code in `lookupByLngLat`.
 *
 * @param {{distance: number, type?: string}} a
 * @param {{distance: number, type?: string}} b
 * @returns {number}
 */
export function compareLookupResults(a, b) {
  const d = a.distance - b.distance;
  if (Math.abs(d) < TIED_DISTANCE_THRESHOLD) {
    return (
      (FEATURE_SPECIFICITY[b.type] || 0) - (FEATURE_SPECIFICITY[a.type] || 0)
    );
  }
  return d;
}

function popupHtml(title, secondary) {
  return `<div class="title">${escapeHtml(
    title || ''
  )}</div><div class="secondary">${escapeHtml(secondary || '')}</div>`;
}
const mapFieldCssSelector = '.map-field';
const mapFieldIdAttribute = 'id';
const formSectionClassName = 'bdm-form-section';
const inputValueIdAttribute = 'id';
const leadingCommaRegex = /^,\s?/;
const intersectionRegex = /^.+\s+(and|&)\s+.+$/i;
const nsewRegex = /^(nw|northwest|north\swest|ne|northeast|north\seast|se|southeast|south\seast|sw|southwest|south\swest|west|w|east|e|north|n|south|s)\s/i;

// TODO create generic version
const categories = [
  ['public transit station', 'public_transportation_station'],
  ['bus station', 'bus_station'],
];

// List of initial bugs we found but fixed, but leaving here as a reference for potential edge cases and to verify they are fixed in the future. Can remove after some more lower env testing:
// create es version
// "Closest Intersection" doesn't match
// need to check matching on Last Name (it was greyed out with address field near it, possible fuzzy match issue)
// "apartment #" we want to unmatch, want to let them type it in.
// should we be matching on 'postal code extension' and/or 'county'
// Not recognizing "Highway or closest highway exit" as main map field (when it's in first position)
// need to handle when we only have a single address field writing the correct value (i.e. full address, i.e. highway not having route on it)

// Things to still look into a bit more
// need to confirm "Landmark" as an individual field. If it is, how do we handle "Address" being a field right next to it
// for the "Hutchinson Use of Force" template, do we want 'address' field to have the full address, or fill out 'street' with appropriate data. Current setup is not filling out 'street' field, but new way does. what is expected behavior
// NOTE - if you make an update to an address field as the very first update in a drafted form, not all the fields in the field group will save. but if you try again, or make a first update that's not an address, they will all save properly. (can test with the 'address validation' template)
// verify the error in the console is not happening in dev2 when we switch taps on http://localhost:8080/reports/10977

/**
 * Map field configuration - maps form field labels to address/location data types.
 *
 * Each entry defines how a form field label is matched and what data it receives:
 *
 * @property {string} label - The text matched against form field labels via Jaro-Winkler fuzzy matching (see fuzzy.js).
 *   A form field titled "City" fuzzy-matches the config entry with label "city".
 *
 * @property {string} value - The type identifier assigned to the matched field. Used as:
 *   1. A CSS class on the input element (for DOM-based field group rebuilding in getAddressFieldGroup)
 *   2. The key used in setAddressFieldValues to look up the correct data to write into the field.
 *   For standard fields, this maps directly to a property on result.properties (e.g. 'place' → result.properties.place).
 *   For supplemental fields, this must be globally unique to avoid collisions with standard Mapbox types
 *   (e.g. 'policeGrid_district' vs the standard 'district' which is Mapbox's county-level data).
 *
 * @property {number} [threshold] - Override the default fuzzy match threshold (0.82). Higher = stricter matching.
 *
 * @property {string} [secondary] - Indicates a secondary variant of the value type (e.g. 'block' for address, 'highway' for street).
 *
 * @property {boolean} [supplementalData] - When true, this field's data comes from GIS polygon layers (e.g. police grids)
 *   stored under result.properties._supplemental, not from Mapbox geocoding results.
 *
 * @property {string} [supplementalKey] - The property key to read from result.properties._supplemental.
 *   Required when supplementalData is true. Decouples the unique type identifier (value) from the actual
 *   data key, so 'policeGrid_district' can read from _supplemental['district'] without collision.
 *
 * @property {boolean} [canBeMainField] - When false, prevents this field from being the "main" map field
 *   (the one that receives the map button UI). Supplemental fields should always set this to false.
 *
 * Flow: form field renders → input-text.js calls getMapFieldInfo(label) → fuzzy matches against these configs
 *   → matched types become CSS classes on the input → getAddressFieldGroup reads CSS classes from DOM
 *   → setAddressFieldValues uses the types to write the correct data into each field.
 */
const mapFieldConfigs = [
  { label: 'address', value: 'address' },
  { label: 'home address', value: 'address' },
  { label: 'block', value: 'address', secondary: 'block' },
  { label: 'line 2', value: 'secondary_address' },
  // { label: 'unit', value: 'secondary_address' },
  // { label: 'apt', value: 'secondary_address' },
  // { label: 'apartment', value: 'secondary_address' }, // current code is not matching on apartment, will bring back¸
  { label: 'city', value: 'place' },
  { label: 'state', value: 'region', threshold: 0.86 },
  { label: 'province', value: 'region' },
  { label: 'zip', value: 'postcode' },
  { label: 'postal', value: 'postcode', threshold: 0.86 },
  { label: 'country', value: 'country' },
  { label: 'street', value: 'street' },
  { label: 'cross street', value: 'street' },
  { label: 'highway', value: 'street', secondary: 'highway' }, // do we want to match on 'highway or closest highway exit'
  { label: 'intersection', value: 'intersection' }, // TODO not real type
  { label: 'closest intersection', value: 'intersection' },
  // { label: 'county', value: 'district' }, // do we want to include? we are matching on county, but not for the word county
  { label: 'district', value: 'district' },
  { label: 'prefecture', value: 'district' },
  { label: 'village', value: 'locality' },
  { label: 'lat', value: 'latitude', threshold: 0.85 },
  { label: 'long', value: 'longitude' },
  { label: 'latitude', value: 'latitude' },
  { label: 'longitude', value: 'longitude' },
  { label: 'neighborhood', value: 'neighborhood' },
  // { label: 'block', value: 'block' }, // Japanese addresses do not use
  { label: 'landmark', value: 'address' },
  // { label: 'grid', value: 'grid', supplementalData: true, supplementalKey: 'grid', canBeMainField: false },
  // Police grid fields - auto-populated from GIS polygon layer data (e.g. miami-dade police grid)
  {
    label: 'police grid name',
    value: 'policeGrid_name',
    supplementalData: true,
    supplementalKey: 'name',
    canBeMainField: false,
  },
  {
    label: 'police grid agency',
    value: 'policeGrid_agency',
    supplementalData: true,
    supplementalKey: 'agency',
    canBeMainField: false,
  },
  {
    label: 'police grid cad agency',
    value: 'policeGrid_cadAgency',
    supplementalData: true,
    supplementalKey: 'cadAgency',
    canBeMainField: false,
  },
  {
    label: 'police grid cad name',
    value: 'policeGrid_cadName',
    supplementalData: true,
    supplementalKey: 'cadName',
    canBeMainField: false,
  },
  {
    label: 'police grid',
    value: 'policeGrid_grid',
    supplementalData: true,
    supplementalKey: 'grid',
    canBeMainField: false,
  },
  {
    label: 'police grid district',
    value: 'policeGrid_district',
    supplementalData: true,
    supplementalKey: 'district',
    canBeMainField: false,
  },
  {
    label: 'police grid district id',
    value: 'policeGrid_districtId',
    supplementalData: true,
    supplementalKey: 'districtId',
    canBeMainField: false,
  },
  {
    label: 'police grid patrol id',
    value: 'policeGrid_patrolId',
    supplementalData: true,
    supplementalKey: 'patrolId',
    canBeMainField: false,
  },
  {
    label: 'police grid region',
    value: 'policeGrid_region',
    supplementalData: true,
    supplementalKey: 'region',
    canBeMainField: false,
  },
  {
    label: 'police grid quadrant',
    value: 'policeGrid_quadrant',
    supplementalData: true,
    supplementalKey: 'quadrant',
    canBeMainField: false,
  },
];

const fieldTypes = [...new Set(mapFieldConfigs.map(v => v.value))];
// Maps supplemental type values to their supplementalKey for direct property lookup on _supplemental
const supplementalKeyMap = new Map(
  mapFieldConfigs
    .filter(v => v.supplementalData)
    .map(v => [v.value, v.supplementalKey])
);
let mapInstanceRefs = new Map();
let layerInteractionsMap = new Map();
let userLngLat;
let interactionPopup;
let interactionFeatureSource;
let interactionFeatureId;
let interactionPopupOpen = false;
let interactionHoverState = false;

export async function getMapboxgl() {
  // helps with the react refs
  return new Promise(resolve => {
    requestAnimationFrame(() => resolve(mapboxgl));
  });
}

export function getMapFieldInfo(inputRef, label) {
  const matches = fuzzy(label, mapFieldConfigs, 0.82);

  // For supplemental data fields (e.g. policeGrid_name, policeGrid_agency),
  // multiple entries will fuzzy-match because they share a long common prefix.
  // Only keep the single best (highest score) match among supplemental entries
  // to prevent one field from mapping to all supplemental values.
  let bestSupplemental = null;
  const nonSupplemental = [];
  for (const match of matches) {
    const config = mapFieldConfigs.find(c => c.value === match.value);
    if (config?.supplementalData) {
      if (!bestSupplemental || match.distance > bestSupplemental.distance) {
        bestSupplemental = match;
      }
    } else {
      nonSupplemental.push(match);
    }
  }

  const matchedTypes = [
    ...new Set([
      ...nonSupplemental.map(v => v.value),
      ...(bestSupplemental ? [bestSupplemental.value] : []),
    ]),
  ];

  const isMapField = matchedTypes.length > 0;
  const canBeMainField = matchedTypes.some(v =>
    mapFieldConfigs.some(b => b.value === v && b.canBeMainField !== false)
  );
  const isMainMapField = !isMapField
    ? false
    : !!canBeMainField &&
      (isMainMapFieldType(matchedTypes) || isFirstMapField(inputRef));

  return {
    isMapField,
    isMainMapField,
    types: matchedTypes,
  };
}

export function addField(mapInstanceRef, inputRef, getValue) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    mapInstanceRefs.set(mapInstanceRef, {
      enabled: false,
      mapInstanceRef,
      inputRefs: new Set(),
      fieldGroup: [],
      layers: [],
      hasAddress: false,
      hasIntersection: false,
      hasIntersectionStreets: false,
      clickMarker: undefined,
      clickMarkerPopup: undefined,
      searchMarker: undefined,
      searchMarkerPopup: undefined,
      valueMarker: undefined,
      valueMarkerPopup: undefined,
      tempMarkers: [],
      clickSuggestions: [],
      searchResult: undefined,
      clickResult: undefined,
      initialResult: undefined,
      selectedResult: undefined,
      searchAbortController: undefined,
      clickAbortController: undefined,
      allowDrag: true,
      dragEndHandlers: {},
      markerClickHandlers: {},
    });
  const mapData = mapInstanceRefs.get(mapInstanceRef);
  if (!mapData.inputRefs.has(inputRef)) mapData.inputRefs.add(inputRef);

  if (mapData.fieldGroup.length === 0) {
    mapData.fieldGroup = getAddressFieldGroup(inputRef.current.input);
    mapData.hasAddress = mapData.fieldGroup.some(v =>
      v.types.includes('address')
    );
    mapData.hasIntersection = mapData.fieldGroup.some(v =>
      v.types.includes('intersection')
    );
    mapData.hasIntersectionStreets =
      mapData.fieldGroup.filter(v => v.types.includes('street')).length > 1;
    mapData.fieldGroupData = getAddressFieldsData(mapData.fieldGroup, getValue);
  }

  const inputId = inputRef.current.input.getAttribute('id');

  return {
    // This tell you what field to show the map button on
    isMainMapField: mapData.fieldGroup.find(v => v.inputId === inputId)
      .isMainMapField,
    hasAddress: mapData.hasAddress,
    hasIntersection: mapData.hasIntersection,
    hasIntersectionStreets: mapData.hasIntersectionStreets,
    fieldGroupData: mapData.fieldGroupData,
  };
}

/**
 * Attaches a debounced dragend listener to a marker. Affordance (cursor,
 * tooltip) is NOT set here — that's owned by `updateMarkerAffordances`,
 * which toggles draggable state based on which marker is "active" (i.e.
 * tied to `selectedResult`).
 *
 * Called once per marker from `enableMap` when `allowDrag` is true. The
 * handler stays attached for the lifetime of the marker; whether it fires
 * is controlled by Mapbox's per-marker `draggable` flag.
 */
function attachDragSupport(mapData, markerName, onDragEnd) {
  const marker = mapData[markerName];
  if (!marker) return;
  if (typeof onDragEnd !== 'function') return;
  const handler = debounce(
    () => handleMarkerDragEnd(mapData, markerName, onDragEnd),
    300
  );
  marker.on('dragend', handler);
  mapData.dragEndHandlers[markerName] = handler;
}

/**
 * Binds the shared `onMarkerClick` handler to a marker so clicking it
 * promotes its current result to `selectedResult`. Only inactive markers
 * carry this binding — the active marker can't be promoted to itself, and
 * leaving it bound would let the synthetic post-drag click (DOM `click`
 * fired on the marker element at mouseup) snap the marker back to its
 * cached coords before the dragend handler reads the drop position.
 * `updateMarkerAffordances` keeps the bound/unbound state in sync with
 * which marker is active.
 */
function attachMarkerClickPromotion(mapData, markerName) {
  const marker = mapData[markerName];
  if (!marker) return;
  if (mapData.markerClickHandlers[markerName]) return;
  const handler = onMarkerClick.bind(marker);
  marker.getElement().addEventListener('click', handler);
  mapData.markerClickHandlers[markerName] = handler;
}

function detachMarkerClickPromotion(mapData, markerName) {
  const handler = mapData.markerClickHandlers[markerName];
  if (!handler) return;
  const marker = mapData[markerName];
  if (marker) marker.getElement().removeEventListener('click', handler);
  delete mapData.markerClickHandlers[markerName];
}

/**
 * Single source of truth for which marker is currently "active" — only the
 * active marker is draggable and shows the drag affordance. Inactive
 * markers are clickable (see `attachMarkerClickPromotion`).
 *
 * Active = the marker whose underlying result matches `selectedResult`. If
 * nothing is explicitly selected but a saved location (`initialResult`)
 * exists, the value (pink) marker is active so the saved location can be
 * dragged-to-refine immediately on form open.
 *
 * Exported as a pure function so the resolution rules are testable without
 * a real mapbox instance.
 *
 * @param {Object} mapData
 * @returns {'searchMarker'|'clickMarker'|'valueMarker'|null}
 */
export function getActiveMarkerName(mapData) {
  const selected = mapData.selectedResult;
  if (selected) {
    if (selected.id === mapData.searchResult?.id) return MARKER_NAMES.SEARCH;
    if (selected.id === mapData.clickResult?.id) return MARKER_NAMES.CLICK;
    if (selected.id === mapData.initialResult?.id) return MARKER_NAMES.VALUE;
  }
  if (mapData.initialResult) return MARKER_NAMES.VALUE;
  return null;
}

function updateMarkerAffordances(mapData) {
  const active = getActiveMarkerName(mapData);
  const draggableAllowed = mapData.allowDrag !== false;
  for (const name of Object.values(MARKER_NAMES)) {
    const marker = mapData[name];
    if (!marker) continue;
    const el = marker.getElement();
    const isActive = name === active;
    const draggable = isActive && draggableAllowed;
    if (typeof marker.setDraggable === 'function') {
      marker.setDraggable(draggable);
    }
    if (draggable) {
      el.style.cursor = 'move';
      el.setAttribute('title', translateText('Drag to refine location'));
    } else {
      el.style.cursor = draggableAllowed ? 'pointer' : '';
      el.removeAttribute('title');
    }
    if (draggableAllowed && !isActive) {
      attachMarkerClickPromotion(mapData, name);
    } else {
      detachMarkerClickPromotion(mapData, name);
    }
  }
}

export async function enableMap(
  mapInstanceRef,
  { allowDrag = true, onDragEnd } = {}
) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  if (mapData.enabled) return;
  mapData.enabled = true;

  mapData.allowDrag = allowDrag;

  if (mapData.clickMarker === undefined) {
    // Markers are constructed non-draggable; `updateMarkerAffordances`
    // toggles `setDraggable(true)` on whichever one is "active" (its
    // result matches `selectedResult`). Only the active marker is
    // draggable; the others remain clickable so a click promotes them.
    mapData.clickMarkerPopup = new mapboxgl.Popup({
      closeButton: true,
      focusAfterOpen: false,
      closeOnClick: false,
    });
    mapData.clickMarker = new mapboxgl.Marker({
      draggable: false,
      color: 'rgb(55, 114, 255)',
      focusAfterOpen: false,
      closeButton: false,
    }).setPopup(mapData.clickMarkerPopup);
    mapData.searchMarkerPopup = new mapboxgl.Popup({
      closeButton: true,
      focusAfterOpen: false,
      closeOnClick: false,
    });
    mapData.searchMarker = new mapboxgl.Marker({
      draggable: false,
      color: ORANGE,
      focusAfterOpen: false,
    }).setPopup(mapData.searchMarkerPopup);
    mapData.valueMarkerPopup = new mapboxgl.Popup({
      closeButton: true,
      focusAfterOpen: false,
      closeOnClick: false,
    });
    mapData.valueMarker = new mapboxgl.Marker({
      draggable: false,
      color: PINK,
      className: 'mapbox-marker-value',
    }).setPopup(mapData.valueMarkerPopup);
    if (allowDrag) {
      for (const name of Object.values(MARKER_NAMES)) {
        attachDragSupport(mapData, name, onDragEnd);
      }
    }
    interactionPopup = new mapboxgl.Popup({
      maxWidth: '280px',
      className: 'mapbox-hover-popup',
      focusAfterOpen: false,
    });
  }
  await buildLayers(mapData);

  let initialCoordinates;
  await getFieldResult(mapInstanceRef);
  if (mapData.initialResult) {
    initialCoordinates = [
      mapData.initialResult.coordinates.longitude,
      mapData.initialResult.coordinates.latitude,
    ];

    mapData.valueMarkerPopup.setHTML(
      popupHtml(mapData.initialResult.label, mapData.initialResult.description)
    );
    mapData.valueMarker.resultId = mapData.initialResult.id;
    mapData.valueMarker
      .setLngLat(initialCoordinates)
      .addTo(mapInstanceRef.current)
      .togglePopup();
  } else {
    initialCoordinates = await getUserLocation();
  }

  zoomToPoints(
    mapInstanceRef,
    [initialCoordinates],
    true,
    mapData.initialResult ? 14 : 10
  );

  updateMarkerAffordances(mapData);

  window.addEventListener(
    'map-result-container-mouseover',
    onResultContainerMouseOver
  );
  window.addEventListener(
    'map-result-container-mouseout',
    onResultContainerMouseOut
  );

  // layer data is needed if we want to render layer controls
  return {
    layers: mapData.layers,
    svgMarker: getMarkerSvg(mapInstanceRef),
    ...buildResultsForDisplay(mapInstanceRef),
  };
}

export async function getFieldResult(mapInstanceRef) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  let address = mapData.fieldGroupData.find(v => v[0] === 'address')?.[1];
  const longitude = mapData.fieldGroupData.find(v => v[0] === 'longitude')?.[1];
  const latitude = mapData.fieldGroupData.find(v => v[0] === 'latitude')?.[1];
  const street = mapData.fieldGroupData.find(v => v[0] === 'street')?.[1];

  if (!address && !longitude && !latitude && !street) return;

  const country = mapData.fieldGroupData.find(v => v[0] === 'country')?.[1];
  const district = mapData.fieldGroupData.find(v => v[0] === 'district')?.[1];
  const place = mapData.fieldGroupData.find(v => v[0] === 'place')?.[1];
  const postcode = mapData.fieldGroupData.find(v => v[0] === 'postcode')?.[1];
  const region = mapData.fieldGroupData.find(v => v[0] === 'region')?.[1];
  const intersection =
    mapData.fieldGroupData.filter(v => v[0] === 'street').length > 1 &&
    [...mapData.fieldGroupData]
      .reverse()
      .find(v => v[0] === 'intersection')?.[1];

  let initialData = formatResult({
    id: 'current',
    type: 'current',
    coordinates: { longitude, latitude },
    properties: {
      address: mapData.fieldGroupData.find(v => v[0] === 'address')?.[1],
      country,
      district,
      place,
      postcode,
      region,
      street,
      intersection,
      longitude,
      latitude,
    },
  });

  if (longitude && latitude) mapData.initialResult = initialData;
  else if (address) {
    /* Mapbox api does not do a good job at address lookup when you are missing parts like country
     * To manage this we attempt to retrieve any location data that is not stored in the field
     * This seems to address the issue of incorrect initial location
     * This also allows us to grab some lat long for proximity
     * */
    const country = (initialData?.properties?.country || '').trim();
    const state = (initialData?.properties?.region || '').trim();
    const city = (initialData?.properties?.place || '').trim();
    const postcode = (initialData?.properties?.postcode || '').trim();
    const district = (initialData?.properties?.district || '').trim();
    const hasEnoughLocation = !!state || !!postcode;
    let locationLatitude;
    let locationLongitude;
    if (hasEnoughLocation) {
      let locationParams = new URLSearchParams({
        country,
        region: state,
        place: city,
        postcode,
        district,
        access_token: accessToken,
      });
      const locationResults = await fetch(
        `https://api.mapbox.com/search/geocode/v6/forward?${locationParams}`,
        {}
      )
        .then(async r => {
          let data = await r.json();
          return data.features || [];
        })
        .catch(e => {
          console.error(e);
          return [];
        });
      const locationResult = locationResults?.[0]?.properties;
      if (locationResult) {
        initialData.properties.region =
          initialData.properties.region ||
          locationResult?.context?.region?.name;
        initialData.properties.place =
          initialData.properties.place || locationResult?.context?.place?.name;
        initialData.properties.postcode =
          initialData.properties.postcode ||
          locationResult?.context?.postcode?.name;
        initialData.properties.district =
          initialData.properties.district ||
          locationResult?.context?.district?.name;
        initialData.properties.country =
          initialData.properties.country ||
          locationResult?.context?.country?.name;
        locationLatitude = locationResult?.coordinates?.latitude;
        locationLongitude = locationResult?.coordinates?.longitude;
        initialData = formatResult(initialData);
      }
    }

    // retrieve initial data so we can center map correctly
    const baseParams = {
      q: initialData.full_address,
      limit: 1,
      access_token: accessToken,
      session_token: sessionToken,
    };
    if (locationLatitude && locationLongitude)
      baseParams.proximity = [locationLongitude, locationLatitude];

    for (let cat of categories) {
      if (address.includes(cat[0])) {
        baseParams.poi_category = cat[1];
        baseParams.q = baseParams.q.replace(cat[0], '');
        break;
      }
    }
    let suggestParams = new URLSearchParams(baseParams);
    const results = await fetch(
      `https://api.mapbox.com/search/searchbox/v1/suggest?${suggestParams}`,
      {}
    )
      .then(async r => {
        let data = await r.json();
        return data.suggestions || [];
      })
      .catch(e => {
        console.error(e);
        return [];
      });
    let suggestions = results.filter(v => v.feature_type !== 'brand'); // brand is used for "View all targets" type of option

    suggestions?.[0] &&
      (await retrieve(mapInstanceRef, suggestions[0], true, initialData));
  }
}

export function destroyMap(mapInstanceRef) {
  if (!mapInstanceRefs.has(mapInstanceRef)) return;
  let mapData = mapInstanceRefs.get(mapInstanceRef);

  window.removeEventListener(
    'map-result-container-mouseover',
    onResultContainerMouseOver
  );
  window.removeEventListener(
    'map-result-container-mouseout',
    onResultContainerMouseOut
  );

  if (mapData?.searchAbortController?.signal?.aborted === false)
    mapData.searchAbortController.abort();
  mapData.searchAbortController = undefined;
  if (mapData?.clickAbortController?.signal?.aborted === false)
    mapData.clickAbortController.abort();
  mapData.clickAbortController = undefined;

  mapData.clickMarkerPopup.remove();
  mapData.clickMarkerPopup = undefined;
  if (mapData.dragEndHandlers) {
    for (const [markerName, handler] of Object.entries(
      mapData.dragEndHandlers
    )) {
      if (typeof handler.cancel === 'function') handler.cancel();
      if (mapData[markerName]) mapData[markerName].off('dragend', handler);
    }
    mapData.dragEndHandlers = {};
  }
  if (mapData.markerClickHandlers) {
    for (const [markerName, handler] of Object.entries(
      mapData.markerClickHandlers
    )) {
      if (mapData[markerName]) {
        mapData[markerName].getElement().removeEventListener('click', handler);
      }
    }
    mapData.markerClickHandlers = {};
  }
  mapData.clickMarker.remove();
  mapData.clickMarker = undefined;
  mapData.searchMarkerPopup.remove();
  mapData.searchMarkerPopup = undefined;
  mapData.searchMarker.remove();
  mapData.searchMarker = undefined;
  if (mapData.valueMarkerPopup) {
    mapData.valueMarkerPopup.remove();
    mapData.valueMarkerPopup = undefined;
  }
  if (mapData.valueMarker) {
    mapData.valueMarker.remove();
    mapData.valueMarker = undefined;
  }

  interactionFeatureSource = undefined;
  interactionFeatureId = undefined;
  interactionPopupOpen = false;
  interactionHoverState = false;
  if (interactionPopup) {
    interactionPopup.remove();
    interactionPopup = undefined;
  }

  clearTempMarkers(mapInstanceRef, true);

  for (let layerData of mapData.layers) {
    for (let interaction of layerData.configUI.interactions || []) {
      switch (interaction.type) {
        case 'hover':
          mapData.mapInstanceRef.current.off(
            'mousemove',
            interaction.layerId,
            onLayerMouseOver
          );
          mapData.mapInstanceRef.current.off(
            'mouseout',
            interaction.layerId,
            onLayerMouseOut
          );
          break;
        case 'click':
          mapData.mapInstanceRef.current.off(
            'click',
            interaction.layerId,
            onLayerClick
          );
          break;
      }
    }

    for (let layer of layerData.layers) {
      if (mapInstanceRef.current.getLayer(layer.id))
        mapInstanceRef.current.removeLayer(layer.id);
    }
  }

  // NOTE there is a bug in mapboxgl with source cleanup
  // all layers need to be removed first, that is why we have a second loop to cleanup the sources
  for (let layerData of mapData.layers) {
    if (layerData.configData.type === 'arcgis' && layerData.arcGisSource)
      layerData.arcGisSource.destroySource();
    else if (mapInstanceRef.current.getSource(layerData.id))
      mapInstanceRef.current.removeSource(layerData.id);
  }

  layerInteractionsMap.clear();

  mapData.layers.length = 0;
  mapData.fieldGroup.length = 0;
  mapData.inputRefs.clear();
  mapData.mapInstanceRef = undefined;
  mapData.clickSuggestions.length = 0;
  mapInstanceRefs.delete(mapInstanceRef);
  mapData = undefined;
}

/**
 * Writes the selected address/location data into the form fields within the field group.
 *
 * Maps each field's matched type(s) to the corresponding property from the selected address data,
 * then dispatches a 'form-component-map-value-change' CustomEvent so each input-text component
 * can pick up its new value and trigger onBlur → Redux sync → auto-save.
 *
 * @param {React.RefObject} mapInstanceRef - Ref to the mapbox-gl map instance, used as a key to look up field group data.
 * @param {Object} selectedAddressData - The address/location data the user selected from the map results.
 *   This is the object built by formatResult() and enriched with layer data. Shape:
 *   {
 *     full_address: string,           // e.g. "123 Main St, Miami, FL 33101, United States"
 *     coordinates: { longitude, latitude },
 *     properties: {
 *       address, street, intersection, place, region, postcode, country, district,
 *       longitude, latitude,
 *       _supplemental: { name, agency, grid, district, ... }  // GIS polygon layer data (e.g. police grid)
 *     }
 *   }
 */
export function setAddressFieldValues(mapInstanceRef, selectedAddressData) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  let values;
  // fill out single fields with full data
  if (mapData.fieldGroup.length === 1) {
    values = {
      [mapData.fieldGroup[0].inputId]: selectedAddressData.full_address,
    };
  } else {
    let isSecondStreet = false;
    values = Object.fromEntries(
      mapData.fieldGroup.map(field => [
        field.inputId,
        field.types
          .map(type => {
            // Handle supplemental data fields (e.g. police grid).
            // Uses the pre-computed supplementalKeyMap to look up the property key on _supplemental.
            const suppKey = supplementalKeyMap.get(type);
            if (suppKey) {
              return (
                selectedAddressData.properties?._supplemental?.[suppKey] || ''
              );
            }

            // handle field group for intersection / cross street fields
            if (type === 'street') {
              if (!isSecondStreet) isSecondStreet = true;
              else return selectedAddressData.properties.intersection || '';
            }

            // Backwards compatibility with current behavior
            if (type === 'address')
              return (
                selectedAddressData?.full_address ||
                selectedAddressData.properties?.[type] ||
                ''
              );

            return selectedAddressData.properties?.[type] || '';
          })
          .filter(v => !!v)
          .join(', '),
      ])
    );
  }

  // always add lng and lat so it can be used if needed
  values.longitude = selectedAddressData.coordinates.longitude;
  values.latitude = selectedAddressData.coordinates.latitude;
  // values.details = structuredClone(selectedAddressData); if we need to store the full data set in snapshot

  window.dispatchEvent(
    new CustomEvent('form-component-map-value-change', { detail: values })
  );
}

export async function suggest(
  mapInstanceRef,
  value,
  types = 'poi,address,neighborhood,region,postcode,district,place,locality'
) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  if (mapData?.searchAbortController?.signal?.aborted === false)
    mapData.searchAbortController.abort();
  mapData.searchAbortController = new AbortController();

  const lngLat = mapInstanceRef.current.getCenter();
  const sw = mapInstanceRef.current.getBounds().getSouthWest();
  const ne = mapInstanceRef.current.getBounds().getNorthEast();
  const viewBounds = [sw.lng, sw.lat, ne.lng, ne.lat];
  const tenMileBounds = buildRadiusBoundsApproximation(
    lngLat.lng,
    lngLat.lat,
    10
  );

  let baseParams = {
    q: value,
    limit: 5,
    proximity: [lngLat.lng, lngLat.lat],
    // Restrict to the US so fuzzy/numeric queries don't surface far-flung global
    // matches (e.g. "836" pulling back German addresses 7,000+ km from the user).
    // Same `country` param works on both /searchbox/v1/suggest and /geocoding/v6/forward.
    country: 'us',
    access_token: accessToken,
  };
  const searchParams = new URLSearchParams({
    ...baseParams,
    session_token: sessionToken,
    origin: [lngLat.lng, lngLat.lat],
    types,
  });
  const forwardParams = new URLSearchParams({
    ...baseParams,
    bbox: getBiggerBounds(viewBounds, tenMileBounds),
  });

  const [searchResp, geocodeResp] = await Promise.all([
    fetch(
      `https://api.mapbox.com/search/searchbox/v1/suggest?${searchParams}`,
      { signal: mapData.searchAbortController.signal }
    )
      .then(async r => {
        let data = await r.json();
        return data.suggestions || [];
      })
      .catch(e => {
        console.error(e);
        return [];
      }),
    fetch(`https://api.mapbox.com/search/geocode/v6/forward?${forwardParams}`, {
      signal: mapData.searchAbortController.signal,
    })
      .then(async r => {
        let data = await r.json();
        return data.features || [];
      })
      .catch(e => {
        console.error(e);
        return [];
      }),
  ]);

  let searchResults = searchResp
    .filter(v => v.feature_type !== 'brand') // brand is used for "View all targets" type of option
    .map(formatResult);

  let geoResults = geocodeResp
    .map(v => {
      const isIntersection =
        v.properties.coordinates?.accuracy === 'intersection';
      return {
        distance: planarDistanceMilesSquared(
          v.properties.coordinates.longitude,
          v.properties.coordinates.latitude,
          lngLat.lng,
          lngLat.lat
        ),
        context: v.properties.context,
        feature_type: v.properties.feature_type,
        mapbox_id: v.properties.mapbox_id,
        name: v.properties.name,
        name_preferred: v.properties.name,
        full_address: `${v.properties.name}, ${v.properties.place_formatted}`,
        place_formatted: v.properties.place_formatted,
        coordinates: v.properties.coordinates,
        _geometry: v.geometry,

        intersection: !isIntersection
          ? undefined
          : v.properties.name
              .split(/\s(and|\&)\s/)
              .find(
                name =>
                  !name
                    .toLowerCase()
                    .includes(v.properties.context.street.name.toLowerCase())
              ),
      };
    })
    .filter(v => v.distance < 10000);

  return mergeSuggestAndGeoResults(searchResults, geoResults);
}

export async function retrieve(
  mapInstanceRef,
  suggestion,
  initial = false,
  initialData
) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  if (mapData?.searchAbortController?.signal?.aborted === false)
    mapData.searchAbortController.abort();
  mapData.searchAbortController = new AbortController();

  let result;
  // If the result has _geometry that means it came from the goecode api and you cannot use that id on the search api
  // Because of this we do what mapbox does with "custom searches"
  if (suggestion._geometry) {
    // mimic mapbox-gl retrieve code
    result = {
      type: 'Feature',
      properties: suggestion,
      geometry: suggestion._geometry,
    };
  } else {
    const params = new URLSearchParams({
      access_token: accessToken,
      session_token: sessionToken,
      types: 'poi,address,neighborhood,region,postcode,district,place,locality',
    });
    const results = await fetch(
      `https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id ||
        suggestion.id}?${params}`,
      { signal: mapData.searchAbortController.signal }
    )
      .then(async r => {
        let data = await r.json();
        return data.features || [];
      })
      .catch(e => {
        console.error(e);
        return [];
      });
    result = results?.[0];
  }

  if (!result) return;

  const newResult = formatResult(result);
  if (initial) {
    // update result with data from fields on initial find
    const country = newResult.properties.country;
    if (!initialData.place_formatted.toLowerCase().includes(country))
      initialData.place_formatted += `, ${country}`;
    if (!initialData.full_address.toLowerCase().includes(country))
      initialData.full_address += `, ${country}`;

    newResult.full_address = initialData.full_address || newResult.full_address;
    newResult.name = initialData.name || newResult.name;
    newResult.label = initialData.label || newResult.label;
    // newResult.description = initialData.description || newResult.description;
    newResult.properties.intersection =
      initialData?.properties?.intersection ||
      newResult.properties.intersection;
    newResult.properties.address =
      initialData?.properties?.address || newResult.properties.address;
    mapData.initialResult = newResult;
    return;
  }

  // Enrich search result with supplemental GIS layer data (e.g. police grid).
  // Click results get this via getLayerDataFromClick which queries ALL layers,
  // but here we only need to query supplemental layers — skip street/intersection/etc.
  // layers entirely to avoid unnecessary API calls.
  if (newResult.coordinates) {
    const supplementalData = await getSupplementalLayerData(
      mapData,
      newResult.coordinates.longitude,
      newResult.coordinates.latitude
    );
    if (supplementalData) {
      newResult.properties._supplemental = supplementalData;
    }
  }

  setResult(mapInstanceRef, newResult, 'search');
  let points = [
    [newResult.coordinates.longitude, newResult.coordinates.latitude],
  ];
  let nonSearchMarker = mapData.mapInstanceRef.current._markers.find(
    v => v !== mapData.searchMarker
  );
  if (nonSearchMarker) points.push(nonSearchMarker.getLngLat());
  zoomToPoints(mapInstanceRef, points);

  return buildResultsForDisplay(mapInstanceRef);
}

export async function lookupByLngLat(
  mapInstanceRef,
  lng,
  lat,
  { skipZoom = false } = {}
) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  if (mapData?.clickAbortController?.signal?.aborted === false)
    mapData.clickAbortController.abort();
  mapData.clickAbortController = new AbortController();

  // Dragend skips the recenter/zoom — the user is already looking at the
  // drop point and an extra flyTo at zoom 15 feels jarring. Map clicks
  // (where the click can be off-screen of the current marker) still recenter.
  if (!skipZoom) {
    let points = [[lng, lat]];
    if (mapData.mapInstanceRef.current._markers.includes(mapData.searchMarker))
      points.push(mapData.searchMarker.getLngLat());
    zoomToPoints(mapInstanceRef, points);
  }

  const [layerData, searchData] = await Promise.all([
    getLayerDataFromClick(mapData, lng, lat, mapData.clickAbortController),
    searchLngLat(lng, lat, mapData.clickAbortController),
  ]);

  /* There is no standard layer data. To provide information like (country, district, ...) we will need to has a secondary source
   * To achieve this we pull the best matching result from the mapbox geocoding api and merge that data in
   * Currently we either find a mapbox result with the same street name or the closest distance
   */
  let streetData = layerData.streets
    .filter(v => !!v?.properties?.address && !!v?.properties?.street)
    .map(street => {
      const mapboxStreet = searchData.find(
        v =>
          // TODO review using includes
          v.properties.feature_type === 'street' &&
          v.properties.context?.street?.name.includes(street.properties.street)
      );

      const mapboxItem = mapboxStreet || searchData[0];

      street.properties.address =
        street.properties.address || street.properties.label; // TODO - sometimes this is empty (i.e. if we click into and around miami airport, we get empty results, so they all look the same, and has 'undefined' in it's full address. AND clicking the item from the field, doesn't do a great job and going to the exact location)
      street.properties.country =
        street.properties.country ||
        mapboxItem.properties.context?.country?.name;
      street.properties.district =
        street.properties.district ||
        mapboxItem.properties.context?.district?.name;
      street.properties.place =
        street.properties.place || mapboxItem.properties.context?.place?.name;
      street.properties.postcode =
        street.properties.postcode ||
        mapboxItem.properties.context?.postcode?.name;
      street.properties.region =
        street.properties.region || mapboxItem.properties.context?.region?.name;

      const place_formatted = `${street.properties.place || ''}${
        street.properties.region ? `, ${street.properties.region}` : ''
      }${street.properties.postcode ? ` ${street.properties.postcode}` : ''}${
        street.properties.country ? `, ${street.properties.country}` : ''
      }`;
      const full_address = `${street.properties.address} ${place_formatted}`;
      street.description = place_formatted;
      street.place_formatted = place_formatted;
      street.full_address = full_address;
      return street;
    });

  // Mapbox will sometime return the same item with different ids
  let searchResultDedupSet = new Set();
  let searchFormatted = searchData
    .filter(v => {
      if (
        searchResultDedupSet.has(v.properties.mapbox_id || v.properties.id) ||
        searchResultDedupSet.has(v.properties.full_address)
      )
        return false;
      searchResultDedupSet.add(v.properties.mapbox_id || v.properties.id);
      if (!!v.properties.full_address)
        searchResultDedupSet.add(v.properties.full_address);
      return true;
    })
    .map(formatResult);
  const returnResults = [...streetData, ...searchFormatted]
    .sort(compareLookupResults)
    .slice(0, clickResultsLimit);
  // Merge supplemental data into search/mapbox results that don't already have it.
  // When the point intersects 2+ polygons, pickClosestByCentroid resolves the
  // ambiguity deterministically (nearest-centroid) instead of last-write-wins
  // across all intersecting polygons. See AISDTEST-301 PLAN.md Phase 3.
  if (layerData.supplemental && layerData.supplemental.length > 0) {
    for (const result of returnResults) {
      if (result.properties._supplemental) continue;
      if (!result.coordinates) continue;
      const pointGeom = {
        type: 'Point',
        coordinates: [
          result.coordinates.longitude,
          result.coordinates.latitude,
        ],
      };
      const candidates = layerData.supplemental.filter(
        sup => sup.geometry && geometryAreIntersecting(pointGeom, sup.geometry)
      );
      const chosen = pickClosestByCentroid(
        [result.coordinates.longitude, result.coordinates.latitude],
        candidates
      );
      if (chosen) {
        const supData = {};
        for (const [key, value] of Object.entries(chosen.properties)) {
          if (key === 'id' || key === 'dedupId') continue;
          supData[key] = value;
        }
        if (Object.keys(supData).length > 0) {
          result.properties._supplemental = supData;
        }
      }
    }
  }

  mapData.clickSuggestions = returnResults;

  // Cluster results that share a rooftop/anchor so the circular spread
  // below keeps them individually clickable. Compare the planar distance
  // BETWEEN the two results' coordinates — comparing each result's
  // distance-from-click clusters anything in the same distance band
  // regardless of direction, which produces a flower of unrelated pins.
  const colocatedThresholdSq = 1e-6; // mi² ≈ 5 ft
  let clusterGroups = [];
  let clustered = new Set();
  for (let r1 of returnResults) {
    if (clustered.has(r1.id)) continue;
    if (!r1.coordinates) continue;
    let cluster = new Set([r1]);
    for (let r2 of returnResults) {
      if (r2.id === r1.id) continue;
      if (clustered.has(r2.id)) continue;
      if (!r2.coordinates) continue;
      if (
        planarDistanceMilesSquared(
          r1.coordinates.longitude,
          r1.coordinates.latitude,
          r2.coordinates.longitude,
          r2.coordinates.latitude
        ) < colocatedThresholdSq
      ) {
        cluster.add(r2);
      }
    }
    if (cluster.size > 1) {
      for (const r of cluster) clustered.add(r.id);
      clusterGroups.push(cluster);
    }
  }

  // Spread truly colocated markers around a 24px ring so each is clickable.
  for (let group of clusterGroups) {
    let length = group.size;
    let index = 0;
    for (let item of group) {
      const angle = (index / length) * 2 * Math.PI; // In radians
      item.markerOffsetX = 24 * Math.cos(angle);
      item.markerOffsetY = 24 * Math.sin(angle);
      index += 1;
    }
  }

  clearTempMarkers(mapInstanceRef);

  // make sure we have enough temp markers
  for (let i = mapData.tempMarkers.length; i < returnResults.length; i += 1) {
    let popup = new mapboxgl.Popup({
      className: 'mapbox-popup-temp',
      focusAfterOpen: false,
      closeButton: false,
    });
    let marker = new mapboxgl.Marker({
      color: 'rgb(62 82 139)',
      className: 'mapbox-marker-temp',
    }).setPopup(popup);
    mapData.tempMarkers.push({ marker, popup });
  }

  // set markers and popups
  let index = 0;
  for (let rr of returnResults) {
    mapData.tempMarkers[index].popup.setHTML(
      popupHtml(rr.label, rr.description)
    );

    mapData.tempMarkers[index].marker.resultId = rr.id;
    mapData.tempMarkers[index].marker
      .setOffset([rr.markerOffsetX || 0, rr.markerOffsetY || 0])
      .setLngLat([rr.coordinates.longitude, rr.coordinates.latitude])
      .addTo(mapInstanceRef.current);

    if (!mapData.tempMarkers[index].onMarkerMouseOver) {
      mapData.tempMarkers[index].onMarkerMouseOver = onMarkerMouseOver.bind(
        mapData.tempMarkers[index].marker
      );
      mapData.tempMarkers[index].onMarkerMouseOut = onMarkerMouseOut.bind(
        mapData.tempMarkers[index].marker
      );
      mapData.tempMarkers[index].onMarkerClick = onMarkerClick.bind(
        mapData.tempMarkers[index].marker
      );
    }
    mapData.tempMarkers[index].popup.setOffset([
      rr.markerOffsetX || 0,
      (rr.markerOffsetY || 0) - 20,
    ]);
    let el = mapData.tempMarkers[index].marker.getElement();
    el.addEventListener(
      'mouseover',
      mapData.tempMarkers[index].onMarkerMouseOver
    );
    el.addEventListener(
      'mouseout',
      mapData.tempMarkers[index].onMarkerMouseOut
    );
    el.addEventListener('click', mapData.tempMarkers[index].onMarkerClick);

    index += 1;
  }

  return buildResultsForDisplay(mapInstanceRef);
}

/**
 * Promotes a result from clickSuggestions / clickResult / searchResult to
 * the selected state and routes it to the correct marker.
 *
 * @param {Object} mapInstanceRef
 * @param {string} resultId
 * @param {'searchMarker'|'valueMarker'} [dragSource] - When provided, the
 *   selection originated from a marker dragend and the result is routed to
 *   the dragged marker (searchMarker → orange, valueMarker → pink) instead
 *   of the default click/search disambiguation. This is how drag-to-refine
 *   avoids spawning a separate blue clickMarker after auto-selecting the
 *   top suggestion.
 */
export function selectResult(mapInstanceRef, resultId, dragSource) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  // Prefer the fresh suggestion from the latest lookup so a re-query that
  // returns the same mapbox_id (with potentially refined coords) replaces
  // the cached object instead of being shadowed by it. Fall back to the
  // cached result only when the id isn't in the fresh suggestions — that
  // covers clicking a persistent marker whose result was set by an earlier
  // lookup or by the initial form-open path.
  let result =
    mapData.clickSuggestions.find(v => v.id === resultId) ||
    (resultId === mapData.clickResult?.id && mapData.clickResult) ||
    (resultId === mapData.searchResult?.id && mapData.searchResult) ||
    (resultId === mapData.initialResult?.id && mapData.initialResult) ||
    null;
  if (!result) {
    console.error('Could not find result by id', resultId);
    return;
  }

  // dragSource (when a dragend triggered this) wins; otherwise infer the
  // type from which result slot the id matches, so clicking an already-
  // shown marker re-promotes its own result instead of spawning a new one.
  const DRAG_SOURCE_TO_TYPE = {
    [MARKER_NAMES.SEARCH]: 'search',
    [MARKER_NAMES.VALUE]: 'value',
    [MARKER_NAMES.CLICK]: 'click',
  };
  let type = DRAG_SOURCE_TO_TYPE[dragSource];
  if (!type) {
    if (resultId === mapData.initialResult?.id) type = 'value';
    else if (resultId === mapData.searchResult?.id) type = 'search';
    else type = 'click';
  }
  setResult(mapInstanceRef, result, type);
  clearTempMarkers(mapInstanceRef);
  mapData.clickSuggestions.length = 0;

  return buildResultsForDisplay(mapInstanceRef);
}

function buildResultsForDisplay(mapInstanceRef) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  return {
    hasResults:
      !!mapData.initialResult ||
      !!mapData.searchResult ||
      !!mapData.clickResult ||
      mapData.clickSuggestions.length > 0,
    initialResult: mapData.initialResult,
    selectedResult: mapData.selectedResult,
    searchResult: mapData.searchResult,
    clickResult: mapData.clickResult,
    clickSuggestions: mapData.clickSuggestions,
  };
}

function clearTempMarkers(mapInstanceRef, destroy = false) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  for (let tempMarker of mapData.tempMarkers) {
    let el = tempMarker.marker.getElement();
    el.removeEventListener('mouseover', tempMarker.onMarkerMouseOver);
    el.removeEventListener('mouseout', tempMarker.onMarkerMouseOut);
    el.removeEventListener('click', tempMarker.onMarkerClick);
    tempMarker.marker.remove();

    if (destroy) {
      tempMarker.onMarkerMouseOver = undefined;
      tempMarker.onMarkerMouseOut = undefined;
      tempMarker.onMarkerClick = undefined;
      tempMarker.popup.remove();
      tempMarker.popup = undefined;
      tempMarker.marker = undefined;
    }
  }

  if (destroy) mapData.tempMarkers.length = 0;
}

function setResult(mapInstanceRef, result, type = 'click') {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  if (!result) {
    console.error('No result');
    return;
  }

  if (mapData.selectedResult) {
    mapData.selectedResult.isSelected = false;
    mapData.selectedResult.isHover = false;
  }

  mapData.selectedResult = result;
  mapData.selectedResult.isHover = false;
  mapData.selectedResult.isSelected = true;

  const target = RESULT_TARGETS[type] || RESULT_TARGETS.click;
  mapData[target.resultField] = result;
  mapData[target.popupField].setHTML(
    popupHtml(result.label, result.description)
  );
  const marker = mapData[target.markerName];
  marker
    .setLngLat([result.coordinates.longitude, result.coordinates.latitude])
    .addTo(mapInstanceRef.current);
  // Stash result.id on the marker element so the shared `onMarkerClick`
  // handler (the same one tempMarkers use) can read `this.resultId` and
  // dispatch the promotion CustomEvent.
  marker.resultId = result.id;
  if (!mapData[target.popupField].isOpen()) marker.togglePopup();
  // Keep popups exclusive — close the other two so only the selected
  // marker's popup is showing.
  for (const otherType of Object.keys(RESULT_TARGETS)) {
    if (otherType === type) continue;
    const other = RESULT_TARGETS[otherType];
    if (mapData[other.popupField].isOpen())
      mapData[other.markerName].togglePopup();
  }

  updateMarkerAffordances(mapData);
}

export function clearResults(mapInstanceRef) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  mapData.clickMarker.remove();
  mapData.searchMarker.remove();
  clearTempMarkers(mapInstanceRef);

  mapData.clickSuggestions.length = 0;
  mapData.searchResult = undefined;
  mapData.clickResult = undefined;
  mapData.selectedResult = undefined;
  updateMarkerAffordances(mapData);
}

export function clearInitialResult(mapInstanceRef) {
  if (!mapInstanceRefs.has(mapInstanceRef)) return;
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  if (mapData.valueMarker) mapData.valueMarker.remove();
  mapData.initialResult = undefined;
  updateMarkerAffordances(mapData);

  return buildResultsForDisplay(mapInstanceRef);
}

export function toggleLayer(mapInstanceRef, layerId) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);

  let layer = mapData.layers.find(v => v.id === layerId);
  if (!layer)
    return console.error(`Could not find map layer with id: ${layerId}`);

  layer.configUI.visible = !layer.configUI.visible;
  if (layer.configData.type === 'arcgis') {
    if (!layer.configUI.visible) layer.arcGisSource.disableRequests();
    else layer.arcGisSource.enableRequests();
  }
  for (let layerData of layer.layers) {
    mapInstanceRef.current.setLayerProperty(
      layerData.id,
      'visibility',
      layer.configUI.visible ? 'visible' : 'none'
    );
  }

  return mapData.layers;
}

export function getMarkerSvg(mapInstanceRef) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');
  const mapData = mapInstanceRefs.get(mapInstanceRef);
  return mapData.clickMarker._element.innerHTML;
}

export function zoomToPoints(
  mapInstanceRef,
  points,
  noAnimation = false,
  zoom = 15
) {
  if (!mapInstanceRefs.has(mapInstanceRef))
    throw Error('no fields found. use add field');

  // need to wait for react to render from searchbox
  requestAnimationFrame(() => {
    if (points.length === 0) return;

    const currentZoom = mapInstanceRef.current.getZoom();

    if (points.length === 1) {
      // Don't zoom out if the user is already zoomed in past the target
      const targetZoom = Math.max(currentZoom, zoom);
      mapInstanceRef.current.flyTo({
        center: points[0],
        zoom: targetZoom,
        animate: !noAnimation,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      });
    } else {
      let zoomBounds = new mapboxgl.LngLatBounds(points[0], points[1]);
      for (let i = 2; i < points.length; i += 1) {
        zoomBounds.extend(points[i]);
      }
      mapInstanceRef.current.fitBounds(zoomBounds, {
        padding: {
          top: 200,
          left: 200,
          right: 420,
          bottom: 200,
        },
        minZoom: currentZoom,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      });
    }
  });
}

function onMarkerMouseOver(event) {
  this.togglePopup();
  window.dispatchEvent(
    new CustomEvent('map-result-marker-mouseover', {
      detail: { resultId: this.resultId },
    })
  );
}

function onMarkerMouseOut(event) {
  this.togglePopup();
  window.dispatchEvent(
    new CustomEvent('map-result-marker-mouseout', {
      detail: { resultId: this.resultId },
    })
  );
}

function onMarkerClick(event) {
  event.preventDefault();
  event.stopPropagation();
  window.dispatchEvent(
    new CustomEvent('map-result-marker-click', {
      detail: { resultId: this.resultId },
    })
  );
}

function onResultContainerMouseOver(event) {
  for (let mapData of Array.from(mapInstanceRefs.values())) {
    for (let tempMarker of mapData.tempMarkers) {
      if (tempMarker.marker.resultId === event.detail.resultId) {
        tempMarker.marker.getElement().classList.add('hover');
        break;
      }
    }
  }
}

function onResultContainerMouseOut(event) {
  for (let mapData of Array.from(mapInstanceRefs.values())) {
    for (let tempMarker of mapData.tempMarkers) {
      if (tempMarker.marker.resultId === event.detail.resultId) {
        tempMarker.marker.getElement().classList.remove('hover');
        break;
      }
    }
  }
}

function formatResult(result) {
  const id =
    result.mapbox_id || result?.properties?.mapbox_id || result.id || 'current';
  const intersection = getValue(result, 'intersection'); // not official mapbox property
  const street = getValue(result, 'street');
  const categoryIds = result?.properties?.poi_category_ids || [];
  const type = getValue(result, 'feature_type', 'current');
  const country = getValue(result, 'country');
  const district = getValue(result, 'district');
  const place = getValue(result, 'place');
  const postcode = getValue(result, 'postcode');
  const region = getValue(result, 'region');
  const longitude =
    result.longitude ||
    result?.properties?.longitude ||
    result?.properties?.coordinates?.longitude ||
    result?.coordinates?.longitude;
  const latitude =
    result.latitude ||
    result?.properties?.latitude ||
    result?.properties?.coordinates?.latitude ||
    result?.coordinates?.latitude;
  const coordinates =
    !longitude || !latitude ? undefined : { longitude, latitude };

  let name = result.name || result?.properties?.name || '';
  let address = getValue(result, 'address', name);
  let place_formatted = `${place || ''}${region ? `, ${region}` : ''}${
    postcode ? ` ${postcode}` : ''
  }${country ? `, ${country}` : ''}`.replace(leadingCommaRegex, '');

  // get place_formatted from address when address = full address
  if (address.includes(place_formatted)) {
    let addressSplit = address.split(', ');
    address = addressSplit[0];
    place_formatted = addressSplit.slice(1).join(', ');
  }

  for (let cat of categories) {
    if (categoryIds.includes(cat[1])) {
      if (typeof address === 'string' && !address.toLowerCase().includes('bus'))
        address += ` ${cat[0]}`;
      if (!name.toLowerCase().includes('bus')) name += ` ${cat[0]}`;
      break;
    }
  }

  const isNameIntersectionMatch = name.match(intersectionRegex) !== null;
  const isIntersection =
    !!intersection || (type !== 'poi' && isNameIntersectionMatch);
  if (isIntersection) {
    if (!isNameIntersectionMatch) {
      name = `${street} ${translate('and')} ${intersection}`;
      address = name;
    } else if (!address) address = name;
  }
  if (!name) name = address;

  // Avoid duplicate leading segments in full_address (e.g. "Miami, Miami, Florida 33144, United States").
  // This happens when address/name is just the city name, which is already the first part of place_formatted.
  const displayAddress = address || name || '';
  const full_address =
    place_formatted.startsWith(displayAddress) &&
    (place_formatted === displayAddress ||
      place_formatted[displayAddress.length] === ',')
      ? place_formatted
      : `${displayAddress}${place_formatted ? `, ${place_formatted}` : ''}`;

  // When the label (`name`) is a POI/business name distinct from the
  // street address, surface the street in the description so result rows
  // and popups show both (e.g. "Apex Restoration & Remodeling" / "44
  // Monroe St · Oswego, IL 60543"). For plain address/street features
  // where name === address, description stays as just place_formatted.
  const description =
    address && name && name !== address && place_formatted
      ? `${address} · ${place_formatted}`
      : address && name && name !== address
      ? address
      : place_formatted;

  return {
    id,
    type,
    label: name,
    name,
    description,
    place_formatted,
    full_address,
    // /suggest returns distance in meters when proximity is set; if it's missing,
    // fall back to a large sentinel so the item naturally falls to the back of any
    // distance-based sort rather than jumping to the front. /forward results build
    // their own distance via planarDistanceMilesSquared and don't pass through here.
    distance: result.distance || 9999,
    coordinates,
    properties: {
      address,
      country,
      district,
      place,
      postcode,
      region,
      street,
      intersection,
      longitude,
      latitude,
    },
  };
}

// This will pull a property from multiple data sources
// mapbox suggestions
// mapbox retrieve
// mapbox reverse
// layer data
// incoming from field
// external
function getValue(result, prop, fallback) {
  return (
    result?.properties?.context?.[prop]?.name ||
    result?.context?.[prop]?.name ||
    result?.properties?.[prop]?.name ||
    result?.properties?.[prop] ||
    result?.[prop] ||
    fallback
  );
}

/**
 * Checks whether a layer's extent is within the current map view bounds.
 * Returns true if the layer should be queried, false if it's out of view.
 */
async function isLayerInView(mapData, layerData) {
  const bounds = mapData.mapInstanceRef.current.getBounds().toArray();
  let maxExtent =
    layerData?.arcGisSource?._maxExtent || layerData.configData.extent;
  if (maxExtent === undefined) {
    layerData.configData.extent = await getArcgisExtent(layerData);
    maxExtent = layerData.configData.extent;
  }

  if (
    Array.isArray(maxExtent) &&
    maxExtent[0] !== -Infinity &&
    (maxExtent[2] < bounds[0][0] ||
      maxExtent[0] > bounds[1][0] ||
      maxExtent[3] < bounds[0][1] ||
      maxExtent[1] > bounds[1][1])
  )
    return false;

  return true;
}

/**
 * Builds and executes a query for a single layer at a given point.
 * Returns a promise that resolves to an array of processed features.
 *
 * Handles arcgis, soda, and rendered (mapbox) layer types.
 * The optional abortController and sodaOpts allow callers to customize behavior:
 *   - abortController: for cancellable requests (used by click path)
 *   - sodaOpts: override soda query params like select, order, limit, where (used by click path
 *     for geometry-type-specific queries like line intersection vs point distance)
 */
function queryLayer(mapData, layerData, lng, lat, abortController, sodaOpts) {
  const fetchOpts = abortController ? { signal: abortController.signal } : {};

  if (layerData.configData.type === 'arcgis') {
    let paramObj = {
      sr: 4326,
      outFields: layerData.configData.outFields || '*',
      spatialRel: 'esriSpatialRelIntersects',
      f: 'geojson',
      returnGeometry: true,
    };

    if (layerData.geometryType !== 'esriGeometryEnvelope') {
      paramObj.geometryType = 'esriGeometryPoint';
      paramObj.geometry = JSON.stringify({
        x: lng,
        y: lat,
        spatialReference: { wkid: 4326 },
      });
      paramObj.distance =
        layerData.configData.lookupRadius * 2 * oneMileInMeters;
      paramObj.units = 'esriSRUnit_Meter';
    } else {
      const bounds = buildRadiusBoundsApproximation(
        lng,
        lat,
        layerData.configData.lookupRadius
      );
      paramObj.geometryType = 'esriGeometryEnvelope';
      paramObj.geometry = JSON.stringify({
        spatialReference: { latestWkid: 4326, wkid: 4326 },
        xmin: bounds[0],
        ymin: bounds[1],
        xmax: bounds[2],
        ymax: bounds[3],
      });
    }

    const params = new URLSearchParams(paramObj);
    return fetch(`${layerData.source.url}/query?${params}`, fetchOpts)
      .then(async r => {
        const data = await r.json();
        return (data.features || []).map(f =>
          processLayerData(f, layerData, lng, lat)
        );
      })
      .catch(e => {
        console.error(e);
        return [];
      });
  } else if (layerData.configData.type === 'soda') {
    const metersFromMiles = layerData.configData.lookupRadius * oneMileInMeters;
    const defaults = {
      select: '*',
      where: `within_circle(${layerData.configData.geometryColumn},${lat},${lng},${metersFromMiles})`,
      order: '',
      limit: 5,
    };
    const opts = { ...defaults, ...sodaOpts };

    const params = new URLSearchParams({
      $select: opts.select,
      $where: opts.where,
      $order: opts.order,
      $limit: opts.limit,
      ...layerData.configData?.queryParams,
    });
    return fetch(`${layerData.source.url}?${params}`, fetchOpts)
      .then(async r => {
        const data = await r.json();
        return (data.features || []).map(f =>
          processLayerData(f, layerData, lng, lat)
        );
      })
      .catch(e => {
        console.error(e);
        return [];
      });
  } else {
    // rendered features from the map
    const bounds = buildRadiusBoundsApproximation(
      lng,
      lat,
      layerData.configData.lookupRadius || 0.01
    );
    const data = mapData.mapInstanceRef.current
      .queryRenderedFeatures(
        [
          mapData.mapInstanceRef.current.project([bounds[0], bounds[1]]),
          mapData.mapInstanceRef.current.project([bounds[2], bounds[3]]),
        ],
        { target: { layerId: layerData.configData.lookupLayer } }
      )
      .map(r => processLayerData(r, layerData, lng, lat));
    return Promise.resolve(data);
  }
}

/**
 * Queries ONLY supplemental GIS layers (e.g. police grid polygons) for a given point.
 * Returns the merged supplemental properties, or null if no supplemental layers exist
 * or the point doesn't intersect any supplemental polygons.
 */
async function getSupplementalLayerData(mapData, lng, lat) {
  const supplementalLayers = mapData.layers.filter(
    l => l.enabled !== false && !!l.configData?.supplemental
  );
  if (supplementalLayers.length === 0) return null;

  const pointGeom = { type: 'Point', coordinates: [lng, lat] };
  const apiPromises = [];

  for (const layerData of supplementalLayers) {
    if (!(await isLayerInView(mapData, layerData))) continue;
    if (
      layerData.configData.type === 'soda' &&
      !layerData.configData.geometryColumn
    )
      continue;
    apiPromises.push(queryLayer(mapData, layerData, lng, lat));
  }

  const allResults = (await Promise.all(apiPromises)).flat();
  const supData = {};
  for (const item of allResults) {
    if (item.geometry && geometryAreIntersecting(pointGeom, item.geometry)) {
      for (const [key, value] of Object.entries(item.properties)) {
        if (key === 'id' || key === 'dedupId') continue;
        supData[key] = value;
      }
    }
  }

  return Object.keys(supData).length > 0 ? supData : null;
}

async function getLayerDataFromClick(mapData, lng, lat, abortController) {
  let other = [];
  let streets = [];
  let mileMarkers = [];
  let intersections = [];
  let blocks = [];
  let supplemental = [];
  let apiData = [];

  for (let layerData of mapData.layers) {
    if (!layerData?.enabled) continue;
    if (!(await isLayerInView(mapData, layerData))) continue;

    if (layerData.configData.type === 'soda') {
      // to lookup by bounding box on soda we need to know what column the geometry is in
      // We will try to automatically work it out
      // If you are getting errors then manually set layerData.configData.geometryColumn
      if (!layerData.configData.geometryColumn) {
        const configParams = new URLSearchParams({
          $select: '*',
          $$read_from_nbe: true,
          $limit: 1,
        });
        const sodaConfigResp = await fetch(
          `${layerData.source.url}?${configParams}`,
          { signal: abortController.signal }
        );
        const sodaConfigData = await sodaConfigResp.json();
        let geomType = sodaConfigData?.features?.[0]?.geometry?.type?.toLowerCase();
        if (geomType.startsWith('line')) geomType = 'line';
        layerData.configData.geometryColumn = geomType;
        layerData.configData.geometryType = geomType;
      }

      // Build soda-specific query overrides based on geometry type
      let sodaOpts = {};
      switch (layerData.configData.geometryType) {
        case 'point':
          sodaOpts.select = `*,distance_in_meters(${layerData.configData.geometryColumn},'POINT(${lng} ${lat})') as distance`;
          sodaOpts.order = `distance_in_meters(${layerData.configData.geometryColumn},'POINT(${lng} ${lat})')`;
          break;
        case 'line':
          const bounds = buildRadiusBoundsApproximation(
            lng,
            lat,
            layerData.configData.lookupRadius
          );
          sodaOpts.where = `intersects(${layerData.configData.geometryColumn},'POLYGON((${bounds[0]} ${bounds[1]},${bounds[2]} ${bounds[1]},${bounds[2]} ${bounds[3]},${bounds[0]} ${bounds[3]},${bounds[0]} ${bounds[1]}))')`;
          sodaOpts.limit = 100;
          break;
        default:
      }

      apiData.push(
        queryLayer(mapData, layerData, lng, lat, abortController, sodaOpts)
      );
    } else {
      apiData.push(queryLayer(mapData, layerData, lng, lat, abortController));
    }
  }

  apiData = await Promise.all(apiData);
  let sorted = apiData.flat().sort((a, b) => a.distance - b.distance);

  const addedChecker = new Set();
  for (let item of sorted) {
    if (!!item.properties.dedupId) {
      if (addedChecker.has(item.properties.dedupId)) continue;
      addedChecker.add(item.properties.dedupId);
    }

    // Supplemental data (e.g. police grid) gets merged into results, not shown as line items
    if (item.supplemental) {
      supplemental.push(item);
      continue;
    }

    switch (item.type) {
      case 'street':
        streets.push(item);
        break;
      case 'mile-marker':
        mileMarkers.push(item);
        break;
      case 'intersection':
        intersections.push(item);
        break;
      case 'block':
        blocks.push(item);
        break;
      default:
        other.push(item);
    }
  }

  let formattedStreets = streets.flatMap(street => {
    const mileMarker = mileMarkers.find(
      v => v.properties.streetId === street.properties.id
    );

    // find intersection from layer data
    const hasIntersectionId = !!street.properties.intersectionId;
    let intersection = intersections.find(v => {
      if (hasIntersectionId) {
        // some map data will list intersections on both ends of street
        const match1 =
          v.properties.streetId === street.properties.intersectionId;
        const match2 =
          v.properties.streetId === street.properties.intersectionAltId;
        return (
          v.properties.street !== street.properties.street && (match1 || match2)
        ); // TODO ???
      } else {
        return v.properties.streetId === street.properties.id;
      }
    });

    // When the street intersects 2+ supplemental polygons, resolve the
    // ambiguity deterministically via pickClosestByCentroid against the
    // click/drag point. This keeps the click and dragend pipelines
    // symmetric — see AISDTEST-301 PLAN.md Phase 3.
    let supData = {};
    const supCandidates = supplemental.filter(sup =>
      geometryAreIntersecting(
        street.geometry || street._geometry,
        sup.geometry || sup._geometry
      )
    );
    const chosenSup = pickClosestByCentroid([lng, lat], supCandidates);
    if (chosenSup) {
      for (const [key, value] of Object.entries(chosenSup.properties)) {
        if (key === 'id' || key === 'dedupId') continue;
        supData[key] = value;
      }
    }

    let intersectionSplit = (intersection?.properties?.name || '').split(
      intersectionSplitRegex
    );

    // NOTE moved that intersection logic to the reverse street lookup
    // intersection data comes from a "intersection" layer
    // if none exist than get by distance
    // if (!hasIntersectionId && intersections.length === 0) {
    //   intersection = streets.find(v => {
    //     const primaryStreetNameNoDir = street.properties.name.replace(nsewRegex, '').trim();
    //     const secondaryStreetNameNoDir = v.properties.name.replace(nsewRegex, '').trim();
    //     const distance = Math.abs(v.distance - street.distance);
    //     return primaryStreetNameNoDir !== secondaryStreetNameNoDir && distance < 0.005;
    //   });
    // }

    const block = blocks.find(
      v =>
        v.properties.id === street.properties.blockId ||
        v.properties.streetId === street.properties.id
    );
    const hasBlock = !!block || !!street.properties.block;

    // more data will be added in parent method
    const properties = {
      street:
        intersectionSplit.length === 2
          ? intersectionSplit[0]
          : street.properties.name,
      mile: mileMarker?.properties?.mile,
      intersection:
        intersectionSplit.length === 2
          ? intersectionSplit[1]
          : intersection?.properties?.name,
      block: block?.properties?.name,
      latitude: lat,
      longitude: lng,
      _supplemental: Object.keys(supData).length > 0 ? supData : undefined,
    };

    if (!mileMarker && !intersection && !hasBlock) {
      return {
        id: street.properties.id,
        type: 'street',
        distance: street.distance,
        label: street.properties.name,
        description: street.properties.name,
        coordinates: { longitude: lng, latitude: lat },
        full_address: street.properties.name,
        properties,
      };
    }

    let returnResults = [];
    if (mileMarker) {
      const address = `${street.properties.name} - ${translate('mile')}: ${
        mileMarker.properties.name
      }`;
      returnResults.push({
        id: `${street.properties.id}-mile`,
        type: 'street',
        distance: street.distance,
        label: address,
        description: street.properties.name,
        coordinates: { longitude: lng, latitude: lat },
        full_address: street.properties.name,
        properties: {
          ...properties,
          address,
          mile: mileMarker.properties?.mile,
        },
      });
    }

    if (intersection) {
      let intersectionValue =
        intersection.properties.intersection || intersection.properties.name;
      let intersectionParts = (intersectionValue || '').split(
        intersectionSplitRegex
      );
      let streetName =
        intersectionParts.length === 2
          ? intersectionParts[0]
          : street.properties.name;
      let intersectionName =
        intersectionParts.length === 2
          ? intersectionParts[1]
          : intersectionValue;
      const address =
        intersectionParts.length === 2
          ? intersectionValue
          : `${streetName} ${translate('and')} ${intersectionName}`;
      returnResults.push({
        id: `${street.properties.id}-intersection`,
        // Layer-built intersection result ("X and Y") — type stays as
        // 'intersection' so the sort tiebreaker prefers it over a
        // co-located 'address'. Matches the searchLngLat side that also
        // tags client-built intersections with this feature_type.
        type: 'intersection',
        distance: street.distance,
        label: address,
        description: street.properties.name,
        coordinates: { longitude: lng, latitude: lat },
        full_address: street.properties.name,
        properties: {
          ...properties,
          address: properties.address || address,
          street: streetName,
          intersection: intersectionName,
        },
      });
    }

    if (hasBlock) {
      const blockName =
        block?.properties?.name ||
        street.properties.blockName ||
        street.properties.block;
      returnResults.push({
        id: `${street.properties.id}-block`,
        type: 'street',
        distance: street.distance,
        label: blockName,
        description: blockName,
        coordinates: { longitude: lng, latitude: lat },
        full_address: blockName,
        properties: {
          ...properties,
          address: blockName,
          block: block?.properties?.name || street.properties.block,
        },
      });
    }

    return returnResults;
  });

  return {
    streets: formattedStreets.sort((a, b) => a.distance - b.distance),
    other: other.sort((a, b) => a.distance - b.distance),
    supplemental,
    sourceResults: {
      streets,
      mileMarkers,
      intersections,
      blocks,
    },
  };
}

function processLayerData(data, layerData, lng, lat) {
  let coordinates =
    lng && lat
      ? getClosestCoord(data.geometry || data._geometry, lng, lat)
      : undefined;
  return {
    type: layerData.type,
    supplemental: !!layerData.configData.supplemental,
    geometry: data.geometry || data._geometry,
    distance:
      coordinates &&
      planarDistanceMilesSquared(coordinates[0], coordinates[1], lng, lat),
    properties: !layerData.configData.propertyMap
      ? data.properties
      : Object.fromEntries(
          layerData.configData.propertyMap.map(p => {
            let keyValue = [
              p[0],
              p[1]
                .split(',')
                .map(s => {
                  let tSplit = s.split('+');
                  return `${data.properties[tSplit[0]] || ''}${
                    tSplit[1] ? ` ${tSplit[1]}` : ''
                  }`;
                })
                .join(' '),
            ];
            if (p[2] && p[3]) {
              // parse regex to for replacing str
              let regexStrMatch = p[2].match(
                /^\/?(?<reg>[^/]+)\/?(?<tags>[a-z]*)$/i
              );
              let reg = new RegExp(
                regexStrMatch.groups.reg,
                regexStrMatch.groups.tags
              );
              keyValue[1] = keyValue[1].replace(reg, p[3]);
            }
            return keyValue;
          })
        ),
  };
}

// Get features from lng / lat
// does 2 searches: 1. street to get intersections - 2. poi (landmark, place)
async function searchLngLat(lng, lat, abortController) {
  try {
    let baseParams = {
      longitude: lng,
      latitude: lat,
      access_token: accessToken,
    };
    const streetParams = new URLSearchParams({
      ...baseParams,
      types: ['street'],
      limit: 4,
    });
    const otherParams = new URLSearchParams({
      ...baseParams,
      limit: 4,
    });
    const [streetResults, otherResults] = await Promise.all([
      // lookup streets for intersections
      fetch(
        `https://api.mapbox.com/search/geocode/v6/reverse?${streetParams}`,
        { signal: abortController.signal }
      )
        .then(async r => {
          let data = await r.json();
          return data.features || [];
        })
        .catch(e => {
          console.error(e);
          return [];
        }),

      // lookup all things in search api
      fetch(
        `https://api.mapbox.com/search/searchbox/v1/reverse?${otherParams}`,
        { signal: abortController.signal }
      )
        .then(async r => {
          let data = await r.json();
          return data.features || [];
        })
        .catch(e => {
          console.error(e);
          return [];
        }),
    ]);

    for (let street of streetResults) {
      street.distance = planarDistanceMilesSquared(
        street.properties.coordinates.longitude,
        street.properties.coordinates.latitude,
        lng,
        lat
      );
    }

    // build intersection results from street distance
    let intersections = [];
    let intersectedStreetIds = new Set();
    let handledStreets = new Set();
    for (let street of streetResults) {
      const name = street.properties.context.street.name
        .replace(nsewRegex, '')
        .trim();
      if (handledStreets.has(name)) continue;
      handledStreets.add(name);

      for (let street2 of streetResults) {
        const name2 = street2.properties.context.street.name
          .replace(nsewRegex, '')
          .trim();
        // only adds a street to intersections[] if it finds ANOTHER street
        // within intersectingStreetDistanceMax (0.0004 miles ≈ 2 feet)
        if (
          name !== name2 &&
          Math.abs(street.distance - street2.distance) <
            intersectingStreetDistanceMax
        ) {
          const address = `${street.properties.context.street.name} ${translate(
            'and'
          )} ${street2.properties.context.street.name}`;
          const full_address = `${street.properties.context.place.name ||
            ''}, ${street.properties.context.region.name || ''} ${street
            .properties.context.postcode.name || ''}, ${street.properties
            .context.country.name || ''}`;
          street.properties.context.address = { name: address };
          street.properties.intersection =
            street2.properties.context.street.name;
          street.properties.full_address = full_address;
          street.properties.name = address;
          street.properties.name_preferred = address;
          // Override the inherited 'street' feature_type so the sort
          // tiebreaker recognizes this as an intersection — without this
          // the result enters compareLookupResults as a 'street' and loses
          // to a co-located 'address' even though the drop is at a corner.
          street.properties.feature_type = 'intersection';
          // Override the inherited street's coordinates with the drop
          // point. The street's `properties.coordinates` from the v6
          // reverse endpoint is "closest point on this street to the
          // drop" — which often coincides with an address rooftop (e.g.
          // Skyview Lane reporting `-117.633505, 33.880036`, the literal
          // 3427 Skyview Lane house). Using the drop point keeps the pin
          // at the actual corner and saves coords that match what the
          // officer placed, so reopening the form puts the pink pin in
          // the same spot rather than at the unrelated rooftop.
          street.properties.coordinates = {
            ...street.properties.coordinates,
            longitude: lng,
            latitude: lat,
          };
          intersections.push(street);
          intersectedStreetIds.add(street.properties.mapbox_id);
          intersectedStreetIds.add(street2.properties.mapbox_id);
        }
      }
    }

    // include standalone streets that weren't paired into an intersection
    const standaloneStreets = streetResults.filter(
      v => !intersectedStreetIds.has(v.properties.mapbox_id)
    );

    // get best match for non street
    const nonStreets = otherResults.map(v => {
      v.distance = planarDistanceMilesSquared(
        v.properties.coordinates.longitude,
        v.properties.coordinates.latitude,
        lng,
        lat
      );
      return v;
    });
    return [...intersections, ...standaloneStreets, ...nonStreets].sort(
      (a, b) => a.distance - b.distance
    );
  } catch (error) {
    console.error('Error during reverse geocoding:', error);
    return [];
  }
}

async function buildLayers(mapData) {
  for (let layerData of layerConfiguration) {
    if (layerData.enabled === false) continue;
    if (layerData.configData.apiOnly === true) {
      mapData.layers.push(layerData);
      continue;
    }

    // set all source layer visibilities based on config visible
    for (let layer of layerData.layers) {
      if (!layer?.layout) layer.layout = {};
      layer.layout.visibility = layerData.configUI.visible ? 'visible' : 'none';
    }

    // setup arcgis feature service data source
    if (layerData.configData.type === 'arcgis') {
      const source = {
        ...layerData.source,
        outFields: layerData.configData.outFields || '*',
        setAttributionFromService:
          layerData.configData.setAttributionFromService,
      };
      let arcGisSource = new FeatureService(
        layerData.id,
        mapData.mapInstanceRef.current,
        source
      );
      mapData.layers.push({ ...layerData, arcGisSource: arcGisSource });
    } else {
      // this will support mapbox and files
      mapData.mapInstanceRef.current.addSource(layerData.id, layerData.source);
      mapData.layers.push(layerData);
    }

    for (let layer of layerData.layers) {
      mapData.mapInstanceRef.current.addLayer(layer);
    }

    for (let interaction of layerData.configUI.interactions || []) {
      if (!layerInteractionsMap.has(interaction.layerId))
        layerInteractionsMap.set(interaction.layerId, layerData);
      if (!layerInteractionsMap.has(layerData.id))
        layerInteractionsMap.set(layerData.id, layerData);
      switch (interaction.type) {
        case 'hover':
          mapData.mapInstanceRef.current.on(
            'mousemove',
            interaction.layerId,
            onLayerMouseOver
          );
          mapData.mapInstanceRef.current.on(
            'mouseout',
            interaction.layerId,
            onLayerMouseOut
          );
          break;
        case 'click':
          mapData.mapInstanceRef.current.on(
            'click',
            interaction.layerId,
            onLayerClick
          );
          break;
      }
    }
  }
}

function onLayerMouseOver(event) {
  removeInteractionHover(event.target);

  const feature = event?.features?.[0];
  const layerData = feature && layerInteractionsMap.get(feature.source);
  const interaction =
    layerData && layerData.configUI.interactions.find(v => v.type === 'hover');

  const clickInteraction =
    layerData && layerData.configUI.interactions.find(v => v.type === 'click');
  if (clickInteraction) event.target.getCanvas().style.cursor = 'pointer';

  if (feature) {
    interactionFeatureId = feature.id;
    interactionFeatureSource = feature.source;
    interactionHoverState = true;
    event.target.setFeatureState(
      { source: interactionFeatureSource, id: interactionFeatureId },
      { hover: true }
    );
  }

  // TODO min max zoom
  if (interaction.popup === true) {
    interactionPopupOpen = true;
    const processed = processLayerData(feature, layerData);
    const displayProps = !interaction?.popupProperties
      ? processed.properties
      : Object.entries(processed.properties).filter(([key]) =>
          interaction.popupProperties.includes(key)
        );
    interactionPopup
      .setLngLat(event.lngLat)
      .setHTML(
        `<span class="title">${escapeHtml(
          layerData.label
        )}</span><span></span>${displayProps
          .map(
            ([key, value]) =>
              `<span class="label">${escapeHtml(
                camelToSentenceCase(key)
              )}:</span><span class="value">${escapeHtml(String(value))}</span>`
          )
          .join('')}`
      )
      .addTo(event.target);
  } else removeInteractionPopup();
}

function onLayerMouseOut(event) {
  event.target.getCanvas().style.cursor = '';

  removeInteractionPopup();
  removeInteractionHover(event.target);
}

function onLayerClick(event) {
  removeInteractionHover(event.target);

  const feature = event?.features?.[0];
  const layerData = feature && layerInteractionsMap.get(feature.source);
  const interaction =
    layerData && layerData.configUI.interactions.find(v => v.type === 'click');

  if (interaction.preventDefault) event.preventDefault();

  // TODO min max zoom
  if (interaction.popup === true) {
    const processed = processLayerData(feature, layerData);
    interactionFeatureId = feature.id;
    interactionFeatureSource = feature.source;
    interactionPopupOpen = true;
    const displayProps = !interaction?.popupProperties
      ? processed.properties
      : Object.entries(processed.properties).filter(([key]) =>
          interaction.popupProperties.includes(key)
        );
    interactionPopup
      .setLngLat(event.lngLat)
      .setHTML(
        `<span class="title">${escapeHtml(
          layerData.label
        )}</span><span></span>${displayProps
          .map(
            ([key, value]) =>
              `<span class="label">${escapeHtml(
                camelToSentenceCase(key)
              )}:</span><span class="value">${escapeHtml(String(value))}</span>`
          )
          .join('')}`
      )
      .addTo(event.target);
  } else removeInteractionPopup();
}

function removeInteractionPopup() {
  if (interactionPopupOpen === false) return;
  interactionPopupOpen = false;
  interactionPopup.remove();
}

function removeInteractionHover(map) {
  if (interactionHoverState === false) return;
  interactionHoverState = false;
  map.setFeatureState(
    { source: interactionFeatureSource, id: interactionFeatureId },
    { hover: false }
  );
  interactionFeatureId = undefined;
  interactionFeatureSource = undefined;
}

function getAddressFieldGroup(inputNode) {
  let parentSection = getParentSection(inputNode);
  if (!parentSection) throw Error('field must be in a section');

  let fields = [...parentSection.querySelectorAll(mapFieldCssSelector)].map(
    (el, i) => {
      let inputElement =
        el.nodeName === 'INPUT' ? el : el.querySelector('input');
      let types = fieldTypes.filter(v => el.classList.contains(v));

      return {
        types,
        isMainMapField: isMainMapFieldType(types) || i === 0,
        inputValueId: inputElement.getAttribute(inputValueIdAttribute),
        inputId: inputElement.getAttribute(mapFieldIdAttribute),
        title: inputElement.getAttribute('title'),
      };
    }
  );

  // if multiple address field groups exist in the same section, then split them based on main field
  let returnFieldGroups = [];
  let mainIndexes = fields
    .map((v, i) => [i, v])
    .filter(v => v[1].isMainMapField)
    .map(v => v[0]);
  if (typeof mainIndexes[0] !== 'number') {
    console.error(
      'map-helper: could not find main field, returning all fields'
    );
    return fields;
  } else if (mainIndexes[0] !== 0) {
    console.error(
      'map-helper: expected main index map field to be first, returning all fields'
    );
    return fields;
  } else {
    mainIndexes.forEach((mainIndex, i) => {
      returnFieldGroups.push(
        fields.slice(mainIndex, mainIndexes[i + 1] || fields.length)
      );
    });

    // find the correct group based on the active main input
    let currentInputId = inputNode.getAttribute('id');
    return returnFieldGroups.find(group =>
      group.some(field => field.inputId === currentInputId)
    );
  }
}

function isMainMapFieldType(types) {
  if (types.length === 0) return false;
  return types.includes('address');
}

function isFirstMapField(inputRef) {
  // check if field is first in section
  if (!inputRef?.current) return false;
  const parent = getParentSection(inputRef.current.input);
  if (!parent) return false;

  const firstMapField = parent.querySelector(`${mapFieldCssSelector} input`);
  return (
    firstMapField &&
    firstMapField.getAttribute(mapFieldIdAttribute) ===
      inputRef.current.props.id
  );
}

function getParentSection(parentNode) {
  let parentSection;
  while (parentNode && parentNode.nodeName !== 'BODY' && !parentSection) {
    parentNode = parentNode.parentElement;
    if (parentNode.classList.contains(formSectionClassName))
      parentSection = parentNode;
  }
  return parentSection;
}

function getAddressFieldsData(fieldGroup, getValueFn) {
  return fieldGroup.flatMap(v => {
    // Prefer the current DOM input value — it reflects the latest selection
    // even if form data hasn't saved yet (e.g. user re-opens map immediately)
    let rawValue = '';
    if (v.inputValueId) {
      const inputEl = document.getElementById(v.inputValueId);
      if (inputEl) rawValue = inputEl.value || '';
    }
    // Fall back to form data if DOM element isn't available
    if (!rawValue) rawValue = getValueFn(v.inputValueId) || '';
    let values = rawValue.split(',').map(v => v.trim());
    let fieldTypeValues = v.types.map((type, i) => {
      let value =
        i === v.types.length - 1
          ? values.join(', ')
          : values.length > 0
          ? values.shift()
          : '';
      return [type, value];
    });

    return fieldTypeValues;
  });
}

async function getUserLocation() {
  if (!navigator.geolocation) return;
  if (userLngLat) return userLngLat;

  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      position => {
        userLngLat = {
          lng: position.coords.longitude,
          lat: position.coords.latitude,
        };
        resolve(userLngLat);
      },
      error => {
        console.error(error);
        resolve();
      }
    );
  });
}

// --- Calculations ----------------------------------------------------------------

function geometryAreIntersecting(geometryA, geometryB) {
  if (!geometryA || !geometryB) return false;

  if (geometryA.type === 'Point' && geometryB.type === 'Polygon') {
    return isPointInPolygon(geometryA.coordinates, geometryB.coordinates);
  } else if (geometryA.type === 'LineString' && geometryB.type === 'Polygon') {
    // Approximation: only checks the first point of the line against the polygon.
    // A line could start outside but pass through — for our use case (short street
    // segments vs large police grid polygons) this is sufficient.
    return isPointInPolygon(geometryA.coordinates[0], geometryB.coordinates);
  }
  console.error(
    'Missing intersection handler for',
    geometryA.type,
    geometryB.type
  );
  return false;
}

function isPointInPolygon(point, polygon) {
  let lng = point[0];
  let lat = point[1];
  let vertices = polygon[0];
  let inside = false;

  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    let xi = vertices[i][0];
    let yi = vertices[i][1];
    let xj = vertices[j][0];
    let yj = vertices[j][1];
    let intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

function buildRadiusBoundsApproximation(lng, lat, radiusInMiles) {
  const latDelta = radiusInMiles / 69.0;
  const lngDelta = radiusInMiles / (69.17 * Math.cos((lat * Math.PI) / 180));
  const minLng = lng - lngDelta;
  const minLat = lat - latDelta;
  const maxLng = lng + lngDelta;
  const maxLat = lat + latDelta;
  return [minLng, minLat, maxLng, maxLat];
}

function getBiggerBounds(bounds1, bounds2) {
  const area1 = (bounds1[2] - bounds1[0]) * (bounds1[3] - bounds1[1]);
  const area2 = (bounds2[2] - bounds2[0]) * (bounds2[3] - bounds2[1]);
  return area1 > area2 ? bounds1 : bounds2;
}

function getClosestCoord(geometry, lng, lat) {
  if (geometry.type === 'Point') return geometry.coordinates;
  else if (geometry.type === 'LineString')
    return getClosestCoordInArray(geometry.coordinates, lng, lat);
  else if (geometry.type === 'MultiLineString')
    return getClosestCoordInArray(
      geometry.coordinates.map(coords =>
        getClosestCoordInArray(coords, lng, lat)
      ),
      lng,
      lat
    );
  else if (geometry.type === 'Polygon')
    return getCenterOfMultiLineString(geometry.coordinates);
}

function getClosestCoordInArray(coords, lng, lat) {
  let sorted = coords
    .map(coord => ({
      distance: planarDistanceMilesSquared(coord[0], coord[1], lng, lat),
      coord,
    }))
    .sort((a, b) => a.distance - b.distance);

  const middleCoord = [
    (sorted[0].coord[0] + sorted[1].coord[0]) / 2,
    (sorted[0].coord[1] + sorted[1].coord[1]) / 2,
  ];
  const middle = {
    distance: planarDistanceMilesSquared(
      middleCoord[0],
      middleCoord[1],
      lng,
      lat
    ),
    coord: middleCoord,
  };

  return [sorted[0], middle, sorted[1]].sort(
    (a, b) => a.distance - b.distance
  )[0].coord;
}

function planarDistanceMilesSquared(lng1, lat1, lng2, lat2) {
  const milesPerDegreeLatAprox = 69.17;
  const avgLat = (lat1 + lat2) / 2;
  const milesPerDegreeLngAprox =
    Math.cos((avgLat * Math.PI) / 180) * milesPerDegreeLatAprox;
  const deltaLat = lat1 - lat2;
  const deltaLon = lng1 - lng2;
  const deltaLatMiles = deltaLat * milesPerDegreeLatAprox;
  const deltaLonMiles = deltaLon * milesPerDegreeLngAprox;
  return deltaLatMiles * deltaLatMiles + deltaLonMiles * deltaLonMiles;
}

function getCenterOfPolygon(points) {
  let lng = 0;
  let lat = 0;
  let area = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    let factor =
      points[i][0] * points[i + 1][1] - points[i + 1][0] * points[i][1];
    lng += (points[i][0] + points[i + 1][0]) * factor;
    lat += (points[i][1] + points[i + 1][1]) * factor;
    area += factor;
  }

  area *= 3;
  lng /= area;
  lat /= area;

  return [lng, lat];
}

/**
 * Deterministic tiebreaker for ambiguous polygon membership: when a point
 * intersects 2+ supplemental polygons, pick the one whose centroid is closest
 * to the point. Used by `lookupByLngLat` and `getLayerDataFromClick` so both
 * the click and dragend pipelines select a single polygon consistently
 * (replaces the pre-Phase-3 non-deterministic multi-polygon property merge).
 *
 * Reuses `getCenterOfPolygon` (shoelace centroid on the outer ring) and
 * `planarDistanceMilesSquared` (equirectangular approximation, US-deployment
 * lat range) — no new math, no new dependencies.
 *
 * Fast paths:
 *   - `polygons.length === 0` (or falsy) → `null`
 *   - `polygons.length === 1` → `polygons[0]` (skip math; matches the pre-
 *     Phase-3 single-merge behavior)
 *
 * Guards (each candidate is skipped silently when it fails):
 *   - `poly.geometry` missing or `poly.geometry.type !== 'Polygon'`
 *   - Outer ring has fewer than 3 points
 *   - Computed centroid contains non-finite values (degenerate / zero-area
 *     polygons; checked via `Number.isFinite`)
 *
 * If every candidate is skipped, the function returns `null` and callers
 * leave the polygon assignment cleared.
 *
 * @param {[number, number]} point - `[lng, lat]` coordinate to compare against.
 *   For dragend, this is the validated marker `getLngLat()`; for the
 *   supplemental-merge inside `lookupByLngLat`, it is the per-result
 *   `result.coordinates.{longitude,latitude}`.
 * @param {Array<{geometry: {type: string, coordinates: Array<Array<[number, number]>>}, properties: Object}>} polygons
 *   GeoJSON Feature-shaped objects (must expose `geometry.type === 'Polygon'`
 *   and `geometry.coordinates[0]` as the outer ring). Typically the
 *   `candidates` array produced by `filter(geometryAreIntersecting(...))`.
 * @returns {Object|null} The closest polygon (one element of `polygons`), or
 *   `null` when the input is empty/falsy or every candidate fails the guards.
 *
 * Known limitations (intentional — see AISDTEST-301 PLAN.md Phase 3 and the
 * Phase 3 Architect Pre-Review):
 *   - Polygon holes (inner rings) are ignored; centroid uses the outer ring
 *     only. Acceptable because supplemental layers in `maps/data/*.json` are
 *     simple polygons.
 *   - MultiPolygon geometries are not handled (current `maps/data/*.json`
 *     entries are all single Polygons).
 *   - Degenerate polygons (area = 0) produce non-finite centroids and are
 *     skipped via `Number.isFinite`.
 *   - Antimeridian crossings are out of scope (US deployment only).
 *
 * @example
 *   const candidates = layerData.supplemental.filter(sup =>
 *     sup.geometry && geometryAreIntersecting(pointGeom, sup.geometry));
 *   const chosen = pickClosestByCentroid([lng, lat], candidates);
 *   if (chosen) { ...merge chosen.properties... }
 */
export function pickClosestByCentroid(point, polygons) {
  if (!polygons || polygons.length === 0) return null;
  if (polygons.length === 1) return polygons[0];

  const [lng, lat] = point;
  let best = null;
  let bestDistance = Infinity;

  for (const poly of polygons) {
    if (!poly?.geometry || poly.geometry.type !== 'Polygon') continue;
    const ring = poly.geometry.coordinates && poly.geometry.coordinates[0];
    if (!ring || ring.length < 3) continue;
    const centroid = getCenterOfPolygon(ring);
    if (!Number.isFinite(centroid[0]) || !Number.isFinite(centroid[1]))
      continue;
    const distance = planarDistanceMilesSquared(
      lng,
      lat,
      centroid[0],
      centroid[1]
    );
    if (distance < bestDistance) {
      bestDistance = distance;
      best = poly;
    }
  }

  return best;
}

function getCenterOfMultiLineString(coordinates) {
  return coordinates
    .map(polygon => getCenterOfPolygon(polygon))
    .reduce(
      (result, coord) => {
        result[0].push(coord[0]);
        result[1].push(coord[1]);
        return result;
      },
      [[], []]
    )
    .map(a => a.reduce((p, c) => p + c, 0) / a.length);
}

function camelToSentenceCase(camelCaseString) {
  const spacedString = camelCaseString.replace(/([A-Z])/g, ' $1');
  const sentenceCaseString =
    spacedString.charAt(0).toUpperCase() + spacedString.slice(1);
  return sentenceCaseString.trim();
}

async function getArcgisExtent(layerData) {
  try {
    const sourceConfigResp = await fetch(`${layerData.source.url}?f=json`, {});
    const sourceConfigData = await sourceConfigResp.json();
    if (!sourceConfigData?.extent) return null;

    // already correct unit (lng, lat)
    if (sourceConfigData?.extent?.spatialReference?.wkid === 4326)
      return [
        sourceConfigData.extent.xmin,
        sourceConfigData.extent.ymin,
        sourceConfigData.extent.xmax,
        sourceConfigData.extent.ymax,
      ];
    // web mercator
    if (sourceConfigData.extent.spatialReference.wkid === 102100)
      return webMercatorToLatLngExtent(sourceConfigData.extent);
    console.warn(
      `No support for coordinate system ${sourceConfigData.extent.spatialReference.wkid} for layer ${layerData.id}. Will need to be added or you can configData.extent = [lngMin, latMin, lngMax, latMax]`
    );
  } catch (e) {
    console.error('Error fetching arcgis extent', e);
    return null;
  }

  return null;
}

function webMercatorToLatLngExtent({ xmin, ymin, xmax, ymax }) {
  // Calculate longitude in degrees
  const lngMin = ((xmin / earthRadiusMeters) * 180) / Math.PI;
  const lngMax = ((xmax / earthRadiusMeters) * 180) / Math.PI;

  // Calculate latitude in degrees using the inverse Gudermannian function
  const latMin =
    ((2 * Math.atan(Math.exp(ymin / earthRadiusMeters)) - Math.PI / 2) * 180) /
    Math.PI;
  const latMax =
    ((2 * Math.atan(Math.exp(ymax / earthRadiusMeters)) - Math.PI / 2) * 180) /
    Math.PI;

  return [lngMin, latMin, lngMax, latMax];
}
